import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  AdjustmentsHorizontalIcon, ArrowDownTrayIcon, ArrowPathIcon, BackspaceIcon, CalendarDaysIcon,
  ChatBubbleBottomCenterTextIcon, CheckCircleIcon, ChevronDownIcon, ClockIcon,
  ChartBarSquareIcon, DevicePhoneMobileIcon, DocumentCheckIcon, ExclamationTriangleIcon, InboxIcon,
  MicrophoneIcon, PauseIcon, PlayIcon,
  PhoneArrowDownLeftIcon, PhoneArrowUpRightIcon, PhoneIcon, PhoneXMarkIcon,
  SignalIcon, SignalSlashIcon, SpeakerWaveIcon, UserGroupIcon, UserPlusIcon, XMarkIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { Sheet } from '../components/Sheet.js';
import { api, ApiError } from '../lib/api.js';
import { useSipPhone, type PhoneDiagnostic, type PhoneState } from '../hooks/useSipPhone.js';
import { usePeerCall, type PeerCallState } from '../hooks/usePeerCall.js';
import { useCallTranscription } from '../hooks/useCallTranscription.js';
import { useNetworkPath, type NetworkPathState } from '../hooks/useNetworkPath.js';
import { useMobileSafetyLocation, type SafetyLocationState } from '../hooks/useMobileSafetyLocation.js';
import { isNativeMobile } from '../mobile/platform.js';
import type { SecureContactCard, SecureContactItem } from '../mobile/secureChannel.js';
import { FieldWellnessScreen } from './FieldWellnessWorkspace.js';
import { SecureMessaging } from './SecureMessaging.js';

export type AgentStatus =
  | 'offline' | 'available' | 'ringing' | 'on_call' | 'after_call_work'
  | 'break' | 'auto_break' | 'meeting_training' | 'unavailable';

type ManualAgentStatus = 'available' | 'break' | 'offline';

export interface CallCenterCall {
  id: string;
  direction: 'inbound' | 'outbound' | 'internal';
  state: string;
  remoteIdentity: string;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  disposition: string | null;
  transcriptCount: number;
}

export interface TranscriptSegment {
  id: string;
  sequence: number;
  speaker: 'agent' | 'caller';
  text: string;
  sourceLanguage: string | null;
  confidence: number | null;
  reviewRequired: boolean;
  startedMs: number;
  endedMs: number;
  createdAt: string;
}

export interface CallCenterData {
  enabled: boolean;
  configured: boolean;
  security: {
    signaling: string;
    media: string;
    protectedData: string;
    mediaE2ee: boolean;
  };
  transcription: {
    enabled: boolean;
    targetLanguage: string;
    retentionDays: number;
    rawAudioStored: boolean;
  };
  communications: {
    access: {
      status: 'not_requested' | 'pending' | 'approved' | 'denied' | 'disabled';
      veritasId: string | null;
      requestedAt: string | null;
      reviewedAt: string | null;
      reason: string | null;
    };
    directory: Array<{
      userId: string;
      displayName: string;
      callable: boolean;
      presence: AgentStatus;
      veritasId: string;
    }>;
    peerConfigured: boolean;
    internalSecurity: string;
    internalCalling: 'blocked_pending_pqe2ee' | 'enabled';
    messaging: 'framework_ready' | 'enabled';
  };
  playbook: PlaybookEntry[];
  agent: {
    extension: string;
    status: AgentStatus;
    priorStatus: AgentStatus | null;
    stateStartedAt: string;
    stateDurationSeconds: number;
    lastQualifyingActivityAt: string;
    stateReason: string | null;
  } | null;
  queues: Array<{ id: string; name: string; extension: string; waiting: number; active: number }>;
  escalationQueue: {
    id: string; name: string; extension: string; availableRecipients: number;
  } | null;
  recentCalls: CallCenterCall[];
  schedule: {
    timezone: string;
    nextEvent: ScheduleEvent | null;
    items: ScheduleEvent[];
  };
  dailyProgress: {
    callsOffered: number;
    callsAnswered: number;
    callsHandled: number;
    appointmentsCreated: number;
    talkSeconds: number;
    status: 'provisional' | 'final';
    asOf: string;
  };
  metricDefinitions: Array<{
    key: string;
    label: string;
    formula: string;
    denominator: string | null;
    exclusions: string;
    minimumSample: number;
    timezoneMode: string;
    refreshSeconds: number;
    version: number;
    effectiveAt: string;
  }>;
  telemetry: {
    latestEventAt: string | null;
    lagSeconds: number | null;
    toleranceSeconds: number;
  };
}

export interface ScheduleEvent {
  id: string;
  type: 'shift' | 'break' | 'lunch' | 'training' | 'meeting' | 'leave' | 'appointment' | 'on_call';
  startsAt: string;
  endsAt: string;
  timezone: string;
  status: 'planned' | 'acknowledged' | 'completed' | 'cancelled';
}

export interface CallCenterContact {
  id: string;
  name: string;
  phone: string;
}

export interface PlaybookEntry {
  id: string;
  category: string;
  title: string;
  triggers: string[];
  response: string;
  followUp: string;
}

const CALL_PLAYBOOK: PlaybookEntry[] = [
  { id: 'welcome', category: 'Opening', title: 'Welcome the caller',
    triggers: ['hello', 'hi', 'calling', 'help'],
    response: 'Thank you for calling Veritas Route Mapping. I am glad to help.',
    followUp: 'What can I help you accomplish today?' },
  { id: 'appointment', category: 'Scheduling', title: 'Schedule an appointment',
    triggers: ['appointment', 'schedule', 'available', 'availability', 'meet', 'visit'],
    response: 'I can help arrange that. Let us find a time that works for you.',
    followUp: 'Which days and times are most convenient?' },
  { id: 'reschedule', category: 'Scheduling', title: 'Change an appointment',
    triggers: ['reschedule', 'change', 'cancel', 'different time', 'cannot make'],
    response: 'I can help update the appointment.',
    followUp: 'What new day and time would work better?' },
  { id: 'confirm', category: 'Verification', title: 'Confirm contact details',
    triggers: ['address', 'phone', 'email', 'contact', 'confirm', 'number'],
    response: 'Let me repeat that back to make sure I recorded it correctly.',
    followUp: 'Would you confirm the name, callback number, and address one more time?' },
  { id: 'sponsor', category: 'Sponsorship', title: 'Sponsorship interest',
    triggers: ['sponsor', 'sponsorship', 'partner', 'partnership', 'support'],
    response: 'Thank you for your interest in working with us. I would like to understand what a successful sponsorship looks like for your organization.',
    followUp: 'Which audience, timing, and goals matter most to you?' },
  { id: 'sponsor-options', category: 'Sponsorship', title: 'Discuss approved options',
    triggers: ['price', 'pricing', 'cost', 'package', 'options', 'benefits'],
    response: 'I can walk through the approved options without making assumptions about what fits best.',
    followUp: 'What budget range and outcome should we plan around?' },
  { id: 'not-interested', category: 'Objection', title: 'Respect a decline',
    triggers: ['not interested', 'no thanks', 'stop calling', 'do not call', 'remove me'],
    response: 'I understand, and I will respect that request.',
    followUp: 'Would you like me to record that no further calls should be made?' },
  { id: 'concern', category: 'Resolution', title: 'Acknowledge a concern',
    triggers: ['problem', 'issue', 'complaint', 'upset', 'wrong', 'frustrated'],
    response: 'I hear the concern, and I want to make sure I understand it accurately before we act.',
    followUp: 'What happened, and what outcome would resolve this for you?' },
  { id: 'verify-answer', category: 'Accuracy', title: 'Do not guess',
    triggers: ['are you sure', 'policy', 'legal', 'promise', 'guarantee', 'deadline'],
    response: 'I do not want to give you inaccurate information. I will verify that before confirming an answer.',
    followUp: 'What is the best way and time to follow up with you?' },
  { id: 'close', category: 'Closing', title: 'Close with confirmation',
    triggers: ['thank you', 'anything else', 'goodbye', 'done', 'that is all'],
    response: 'Before we finish, I will summarize what we agreed to and the next action.',
    followUp: 'Is there anything I missed or anything else you need today?' },
];

const PHONE_LABEL: Record<PhoneState, string> = {
  offline: 'Disconnected', connecting: 'Connecting', ready: 'Ready', ringing: 'Incoming call',
  calling: 'Calling', connected: 'Connected', held: 'On hold', failed: 'Call failed',
};

const PRESENCE_LABEL: Record<AgentStatus, string> = {
  offline: 'Offline',
  available: 'Available',
  ringing: 'Ringing',
  on_call: 'On call',
  after_call_work: 'After call work',
  break: 'Break',
  auto_break: 'Auto Break',
  meeting_training: 'Meeting or training',
  unavailable: 'Unavailable',
};

const DIAL_KEYS = [
  ['1', ''], ['2', 'ABC'], ['3', 'DEF'], ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'],
  ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'], ['*', ''], ['0', ''], ['#', ''],
];

type WorkspaceTab = 'directory' | 'coach' | 'day' | 'security' | 'queues' | 'history';
type NativePhoneTab = 'phone' | 'messages' | 'directory' | 'recents' | 'wellness' | 'settings';

interface WakeLockHandle {
  release: () => Promise<void>;
  addEventListener?: (type: 'release', listener: () => void) => void;
}

interface DeferredInstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const WORKSPACE_TABS: Array<{ id: WorkspaceTab; label: string }> = [
  { id: 'directory', label: 'Directory' },
  { id: 'coach', label: 'Conversation coach' },
  { id: 'day', label: 'My day' },
  { id: 'security', label: 'Call security' },
  { id: 'queues', label: 'Queues' },
  { id: 'history', label: 'Recent calls' },
];

export function CallCenterScreen({
  data, onRefresh, initialContact, connectOnLoad = false, connectRequestId = 0,
  onScheduleVisit, onEditPlaybook, advancedMobileAvailable = false,
}: {
  data: CallCenterData | null;
  onRefresh: () => Promise<void>;
  initialContact?: CallCenterContact | null;
  connectOnLoad?: boolean;
  connectRequestId?: number;
  onScheduleVisit?: (personId: string) => void;
  onEditPlaybook?: () => void;
  advancedMobileAvailable?: boolean;
}) {
  const nativeMobile = isNativeMobile();
  const recordDiagnostic = useCallback((event: PhoneDiagnostic) => {
    void api.recordCallCenterDiagnostic(event).catch(() => undefined);
  }, []);
  const phone = useSipPhone(recordDiagnostic);
  const loadPeerIceServers = useCallback(async () => {
    const channel = await import('../mobile/secureChannel.js');
    return channel.peerIceServers();
  }, []);
  const peer = usePeerCall(loadPeerIceServers, phone.inputDeviceId, phone.outputDeviceId);
  const [number, setNumber] = useState(() => normalizePhone(initialContact?.phone ?? ''));
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [wrapUpCallId, setWrapUpCallId] = useState<string | null>(null);
  const [disposition, setDisposition] = useState('reached');
  const [callNote, setCallNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [credentialRefreshAt, setCredentialRefreshAt] = useState<number | null>(null);
  const [contact, setContact] = useState<CallCenterContact | null>(initialContact ?? null);
  const [captionsPaused, setCaptionsPaused] = useState(false);
  const [audioDevicesOpen, setAudioDevicesOpen] = useState(false);
  const [escalationOpen, setEscalationOpen] = useState(false);
  const [escalationReason, setEscalationReason] = useState('supervisor_support');
  const [escalationDetail, setEscalationDetail] = useState('');
  const [workspaceTab, setWorkspaceTab] = useState<WorkspaceTab>('coach');
  const [nativeTab, setNativeTab] = useState<NativePhoneTab>('phone');
  const [wellnessOpened, setWellnessOpened] = useState(false);
  useEffect(() => { if (nativeTab === 'wellness') setWellnessOpened(true); }, [nativeTab]);
  const [travelMode, setTravelMode] = useState(false);
  const [showAdvancedMobile, setShowAdvancedMobile] = useState(false);
  const [keypadOpen, setKeypadOpen] = useState(() => isNativeMobile());
  const networkPath = useNetworkPath();
  const safetyLocation = useMobileSafetyLocation(Boolean(nativeMobile
    && data?.communications.access.status === 'approved'));
  const [clockNow, setClockNow] = useState(() => Date.now());
  const recordedPhoneState = useRef<PhoneState>('offline');
  const incomingRecording = useRef(false);
  const automaticConnection = useRef<string | null>(null);
  const automaticNativeRegistration = useRef(false);
  const lastActivitySignalAt = useRef(0);
  const canDial = phone.state === 'ready' && isUsPhoneNumber(number);
  const peerActive = ['calling', 'ringing', 'connected'].includes(peer.state);
  const callActive = ['calling', 'connected', 'held'].includes(phone.state)
    || ['calling', 'connected'].includes(peer.state);
  const visiblePhoneState: PhoneState = peer.state === 'calling' ? 'calling'
    : peer.state === 'ringing' ? 'ringing'
      : peer.state === 'connected' ? 'connected'
        : peer.state === 'failed' ? 'failed'
          : nativeMobile && !data?.agent
              && data?.communications.access.status === 'approved' && peer.supported
            ? 'ready' : phone.state;
  const simpleMobile = nativeMobile && !showAdvancedMobile;
  const wakeLock = useRef<WakeLockHandle | null>(null);
  const captions = useCallTranscription({
    enabled: Boolean(data?.transcription.enabled) && !captionsPaused,
    callId: activeCallId,
    active: phone.state === 'connected' || phone.state === 'held',
    agentMuted: phone.muted,
    remoteAudioRef: phone.remoteAudioRef,
  });
  const elapsed = useMemo(() => activeCallId ? 'Call activity is recorded in the audit log.' : null, [activeCallId]);

  useEffect(() => {
    if (nativeMobile && (callActive || phone.state === 'ringing'
        || peer.state === 'ringing' || wrapUpCallId)) {
      setNativeTab('phone');
    }
  }, [nativeMobile, callActive, peer.state, phone.state, wrapUpCallId]);

  useEffect(() => {
    const wakeNavigator = navigator as Navigator & {
      wakeLock?: { request: (type: 'screen') => Promise<WakeLockHandle> };
    };
    if (!callActive || !wakeNavigator.wakeLock) return;
    let stopped = false;
    const acquire = async () => {
      if (stopped || document.visibilityState !== 'visible' || wakeLock.current) return;
      try {
        const handle = await wakeNavigator.wakeLock!.request('screen');
        if (stopped) {
          await handle.release();
          return;
        }
        wakeLock.current = handle;
        handle.addEventListener?.('release', () => {
          if (wakeLock.current === handle) wakeLock.current = null;
        });
      } catch {
        wakeLock.current = null;
      }
    };
    const visibilityChanged = () => { if (document.visibilityState === 'visible') void acquire(); };
    document.addEventListener('visibilitychange', visibilityChanged);
    void acquire();
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', visibilityChanged);
      const handle = wakeLock.current;
      wakeLock.current = null;
      void handle?.release().catch(() => undefined);
    };
  }, [callActive]);

  const markActivity = useCallback((kind: Parameters<typeof api.recordCallCenterActivity>[0]) => {
    const now = Date.now();
    if (now - lastActivitySignalAt.current < 15_000) return;
    lastActivitySignalAt.current = now;
    void api.recordCallCenterActivity(kind)
      .then((result) => {
        if (result.state === 'available') return onRefresh();
      })
      .catch(() => undefined);
  }, [onRefresh]);

  useEffect(() => {
    const clock = window.setInterval(() => setClockNow(Date.now()), 1_000);
    const refresh = window.setInterval(() => {
      if (document.visibilityState === 'visible' && networkPath.connected) {
        void onRefresh().catch(() => undefined);
      }
    }, networkPath.refreshMs);
    return () => {
      window.clearInterval(clock);
      window.clearInterval(refresh);
    };
  }, [onRefresh, networkPath.connected, networkPath.refreshMs]);

  useEffect(() => {
    if (!initialContact) return;
    setContact(initialContact);
    setNumber(normalizePhone(initialContact.phone));
  }, [initialContact]);

  useEffect(() => {
    if (!connectOnLoad || !connectRequestId || !initialContact
        || !data?.enabled || !data.configured || !data.agent) return;
    if (!['offline', 'failed', 'ready'].includes(phone.state)) return;
    const requestKey = `${connectRequestId}:${initialContact.id}`;
    if (automaticConnection.current === requestKey) return;
    automaticConnection.current = requestKey;
    void prepareClickToCall(initialContact);
  }, [connectOnLoad, connectRequestId, initialContact, data, phone.state]);

  useEffect(() => {
    if (!activeCallId || recordedPhoneState.current === phone.state) return;
    recordedPhoneState.current = phone.state;
    const persisted = phone.state === 'connected' ? 'connected'
      : phone.state === 'held' ? 'held'
        : phone.state === 'failed' ? 'failed'
          : null;
    if (!persisted) return;
    const recordedCallId = activeCallId;
    void api.updateCallCenterCall(recordedCallId, { state: persisted })
      .then(() => {
        if (persisted !== 'failed') return;
        setActiveCallId((current) => current === recordedCallId ? null : current);
        return onRefresh();
      })
      .catch((cause) => setMessage(apiMessage(cause)));
  }, [activeCallId, phone.state, onRefresh]);

  useEffect(() => {
    if (!credentialRefreshAt) return;
    const delay = Math.max(0, credentialRefreshAt - Date.now());
    const timer = window.setTimeout(() => {
      if (phone.state !== 'ready') {
        setCredentialRefreshAt(Date.now() + 30_000);
        return;
      }
      void api.callCenterSession()
        .then(async (session) => {
          await phone.connect(session, true);
          setCredentialRefreshAt(new Date(session.expiresAt).getTime() - 60_000);
        })
        .catch((cause) => setMessage(apiMessage(cause)));
    }, delay);
    return () => window.clearTimeout(timer);
  }, [credentialRefreshAt, phone.state, phone.connect]);

  useEffect(() => {
    if (phone.state !== 'ringing' || activeCallId || incomingRecording.current) return;
    incomingRecording.current = true;
    void api.startCallCenterCall({ target: 'withheld', direction: 'inbound' })
      .then((created) => { setCaptionsPaused(false); setActiveCallId(created.id); })
      .catch((cause) => setMessage(apiMessage(cause)))
      .finally(() => { incomingRecording.current = false; });
  }, [activeCallId, phone.state]);

  async function connect(
    requestedContact: CallCenterContact | null = contact ?? initialContact ?? null,
    requestedNumber = number,
    prepareAudio = true,
  ) {
    const selectedContact = requestedContact;
    const selectedNumber = requestedNumber
      || (selectedContact ? normalizePhone(selectedContact.phone) : '');
    if (selectedContact) {
      setContact(selectedContact);
      setNumber(selectedNumber);
    }
    if (prepareAudio) phone.preparePlayback();
    setBusy(true); setMessage(null);
    try {
      const session = await api.callCenterSession();
      await phone.connect(session);
      setCredentialRefreshAt(new Date(session.expiresAt).getTime() - 60_000);
      await api.setCallCenterAgentStatus('available');
      await onRefresh();
    } catch (cause) {
      setMessage(apiMessage(cause));
    } finally {
      if (selectedContact) {
        setContact(selectedContact);
        setNumber(selectedNumber || normalizePhone(selectedContact.phone));
      }
      setBusy(false);
    }
  }

  async function prepareClickToCall(selectedContact: CallCenterContact) {
    const selectedNumber = normalizePhone(selectedContact.phone);
    setContact(selectedContact);
    setNumber(selectedNumber);
    if (phone.state === 'offline' || phone.state === 'failed') {
      await connect(selectedContact, selectedNumber, false);
      return;
    }
    setBusy(true); setMessage(null);
    try {
      await api.setCallCenterAgentStatus('available');
      await onRefresh();
    } catch (cause) {
      setMessage(apiMessage(cause));
    } finally {
      setContact(selectedContact);
      setNumber(selectedNumber);
      setBusy(false);
    }
  }

  async function disconnect() {
    setBusy(true); setMessage(null);
    try {
      await phone.disconnect();
      setCredentialRefreshAt(null);
      await api.setCallCenterAgentStatus('offline');
      await onRefresh();
    } catch (cause) { setMessage(apiMessage(cause)); }
    finally { setBusy(false); }
  }

  async function placeCall() {
    if (!canDial) return;
    phone.prepareCallMedia();
    setBusy(true); setMessage(null);
    let createdCallId: string | null = null;
    try {
      const created = await api.startCallCenterCall({
        target: number,
        direction: 'outbound',
        personId: contact?.id,
      });
      createdCallId = created.id;
      setCaptionsPaused(false);
      setActiveCallId(created.id);
      await phone.call(created.sipTarget, {
        callId: created.id, providerToken: created.providerToken,
      });
    } catch (cause) {
      phone.cancelPreparedMedia();
      if (createdCallId) await api.updateCallCenterCall(createdCallId, { state: 'failed' }).catch(() => undefined);
      setActiveCallId(null);
      setMessage(apiMessage(cause));
    }
    finally { setBusy(false); }
  }

  async function startVeritasCall(veritasId: string) {
    if (data?.communications.access.status !== 'approved' || !isVeritasId(veritasId)
        || !peer.supported || peerActive
        || !['offline', 'failed', 'ready'].includes(phone.state)) return;
    setNativeTab('phone');
    setBusy(true); setMessage(null); setContact(null); setNumber(veritasId);
    try {
      await peer.startCall(veritasId);
    } catch (cause) {
      setMessage(peerCallMessage(cause));
    } finally { setBusy(false); }
  }

  async function callDirectoryEntry(entry: CallCenterData['communications']['directory'][number]) {
    if (data?.communications.access.status !== 'approved'
        || !entry.callable || data.communications.internalCalling !== 'enabled') return;
    await startVeritasCall(entry.veritasId);
  }

  async function callSecureContact(card: SecureContactCard) {
    if (card.veritasId) {
      await startVeritasCall(card.veritasId);
      return;
    }
    const target = card.phone ?? '';
    if (data?.communications.access.status !== 'approved' || !isUsPhoneNumber(target)
        || !['offline', 'failed', 'ready'].includes(phone.state)) return;
    const selectedContact = { id: card.cardId, name: card.displayName, phone: target };
    setNativeTab('phone');
    phone.prepareCallMedia();
    setBusy(true); setMessage(null); setContact(selectedContact); setNumber(target);
    let createdCallId: string | null = null;
    try {
      if (phone.state === 'offline' || phone.state === 'failed') {
        const session = await api.callCenterSession();
        await phone.connect(session);
        setCredentialRefreshAt(new Date(session.expiresAt).getTime() - 60_000);
      }
      await api.setCallCenterAgentStatus('available');
      const created = await api.startCallCenterCall({ target, direction: 'outbound' });
      createdCallId = created.id;
      setCaptionsPaused(false);
      setActiveCallId(created.id);
      await phone.call(created.sipTarget, {
        callId: created.id, providerToken: created.providerToken,
      });
      await onRefresh();
    } catch (cause) {
      phone.cancelPreparedMedia();
      if (createdCallId) {
        await api.updateCallCenterCall(createdCallId, { state: 'failed' }).catch(() => undefined);
      }
      setActiveCallId(null);
      setMessage(apiMessage(cause));
    } finally { setBusy(false); }
  }

  async function endCall() {
    setBusy(true); setMessage(null);
    try {
      await phone.hangup();
      if (activeCallId) {
        await api.updateCallCenterCall(activeCallId, { state: 'wrap_up' });
        setWrapUpCallId(activeCallId);
      }
      setActiveCallId(null);
      await onRefresh();
    } catch (cause) { setMessage(apiMessage(cause)); }
    finally { setBusy(false); }
  }

  async function endCurrentCall() {
    if (peerActive) {
      setBusy(true); setMessage(null);
      try { await peer.hangup(); }
      catch (cause) { setMessage(peerCallMessage(cause)); }
      finally { setBusy(false); }
      return;
    }
    await endCall();
  }

  async function changeAgentStatus(status: ManualAgentStatus) {
    setBusy(true); setMessage(null);
    try { await api.setCallCenterAgentStatus(status); await onRefresh(); }
    catch (cause) { setMessage(apiMessage(cause)); }
    finally { setBusy(false); }
  }

  async function saveWrapUp() {
    if (!wrapUpCallId) return;
    setBusy(true); setMessage(null);
    try {
      await api.updateCallCenterCall(wrapUpCallId, {
        state: 'completed', disposition, note: callNote.trim() || undefined,
      });
      setWrapUpCallId(null); setCallNote(''); setDisposition('reached');
      await onRefresh();
    } catch (cause) { setMessage(apiMessage(cause)); }
    finally { setBusy(false); }
  }

  async function requestEscalation() {
    if (!activeCallId || !data?.escalationQueue) return;
    setBusy(true); setMessage(null);
    try {
      const result = await api.escalateCallCenterCall(activeCallId, {
        reasonCode: escalationReason,
        reasonDetail: escalationDetail.trim() || undefined,
      });
      setEscalationOpen(false);
      setEscalationDetail('');
      setMessage(`Transfer requested to ${result.destination.name}. Stay on the line until it completes.`);
      await onRefresh();
    } catch (cause) { setMessage(apiMessage(cause)); }
    finally { setBusy(false); }
  }

  async function requestAccess() {
    setBusy(true); setMessage(null);
    try {
      const result = await api.requestCommunicationAccess();
      setMessage(result.status === 'approved'
        ? 'Your VERITAS phone access is active.'
        : 'Your request was sent to a manager for approval.');
      await onRefresh();
    } catch (cause) { setMessage(apiMessage(cause)); }
    finally { setBusy(false); }
  }

  useEffect(() => {
    if (!nativeMobile || !data?.enabled || !data.configured || !data.agent
        || data.communications.access.status !== 'approved' || !networkPath.connected
        || !['offline', 'failed'].includes(phone.state) || busy
        || automaticNativeRegistration.current) return;
    automaticNativeRegistration.current = true;
    setBusy(true);
    setMessage(null);
    void api.callCenterSession()
      .then(async (session) => {
        await phone.connect(session);
        setCredentialRefreshAt(new Date(session.expiresAt).getTime() - 60_000);
        await api.setCallCenterAgentStatus('available');
        await onRefresh();
      })
      .catch((cause) => setMessage(apiMessage(cause)))
      .finally(() => {
        setBusy(false);
        window.setTimeout(() => { automaticNativeRegistration.current = false; }, 15_000);
      });
  }, [busy, data, nativeMobile, networkPath.connected, onRefresh, phone.connect, phone.state]);

  if (!data) return <CallCenterSkeleton />;

  return (
    <div className={`pt-4 desktop:pt-6 ${travelMode ? 'call-center-travel' : ''} ${nativeMobile ? 'native-field-phone' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Operations</p>
          <h1>{simpleMobile ? 'Field Phone' : 'Call Center'}</h1>
          <p className="text-sm text-neutral-700 mt-1">
            {simpleMobile ? 'Calls and audio controls' : 'Protected browser phone, queues, and call history'}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <NetworkPathIndicator value={networkPath} />
          <div className="flex items-center gap-2 rounded-full border border-neutral-300 px-3 min-h-touch bg-neutral-100">
            <span className={`h-2.5 w-2.5 rounded-full ${visiblePhoneState === 'ready' || callActive ? 'bg-presence-active' : visiblePhoneState === 'failed' ? 'bg-presence-offline' : 'bg-presence-idle'}`} />
            <span className="text-sm font-semibold">{PHONE_LABEL[visiblePhoneState]}</span>
          </div>
        </div>
      </div>

      {!nativeMobile && <MobileInstallControl />}

      {!networkPath.connected && <div className="form-error mb-3" role="alert">
        The phone is offline. Reconnect to Wi-Fi or mobile data before placing a call.
      </div>}

      {nativeMobile && data.communications.access.status === 'approved' && <NativeSecurityStatus
        data={data} phoneState={phone.state}
        peerState={peer.state} peerRemoteVeritasId={peer.remoteVeritasId} />}

      {!nativeMobile && <section className="call-center-mobile-mode desktop:hidden" aria-labelledby="mobile-mode-heading">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <DevicePhoneMobileIcon className="h-5 w-5 text-accent-700" aria-hidden />
            <h2 id="mobile-mode-heading" className="text-base">Hands-free travel mode</h2>
          </div>
          <p className="mt-1 text-xs text-neutral-700">
            Keeps only essential call controls visible. Pair a headset before leaving, keep Veritas open, and park before typing.
          </p>
        </div>
        <button type="button" className={`btn shrink-0 ${travelMode ? 'btn-primary' : 'btn-secondary'}`}
          aria-pressed={travelMode} onClick={() => setTravelMode((current) => !current)}>
          {travelMode ? 'Show all' : 'Simplify'}
        </button>
      </section>}

      {!travelMode && data.agent && <div className="hidden desktop:block">
        <OperatorSummary data={data} now={clockNow}
          onReturnAvailable={() => void changeAgentStatus('available')} busy={busy} />
      </div>}

      {data.communications.access.status !== 'approved' ? (
        <section className="border border-neutral-300 bg-neutral-100 p-4 max-w-[680px]"
          aria-labelledby="communications-access-title">
          <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Managed phone access</p>
          <h2 id="communications-access-title" className="text-h4 mt-1">
            {data.communications.access.status === 'pending'
              ? 'Approval pending'
              : data.communications.access.status === 'disabled'
                ? 'Access disabled'
                : 'Approval required'}
          </h2>
          <p className="text-base text-neutral-700 mt-2">
            A manager must approve this VERITAS identity before it can register, receive calls, or appear in the employee directory.
          </p>
          {data.communications.access.veritasId && <p className="text-sm font-semibold mt-3">
            VERITAS ID <span className="font-mono break-all">{data.communications.access.veritasId}</span>
          </p>}
          {data.communications.access.status !== 'pending'
            && data.communications.access.status !== 'disabled' && <button type="button"
              className="btn btn-primary mt-4" disabled={busy} onClick={() => void requestAccess()}>
              <UserPlusIcon className="h-5 w-5" aria-hidden />
              {busy ? 'Sending…' : 'Request access'}
            </button>}
          {(message || phone.error) && <p className="mt-3 text-sm" role="status">{message ?? phone.error}</p>}
        </section>
      ) : (!data.enabled || (!data.configured && !data.communications.peerConfigured)) ? (
        <section className="border border-neutral-300 bg-neutral-100 p-4 max-w-[680px]" aria-labelledby="phone-unavailable">
          <h2 id="phone-unavailable" className="text-h4">Secure calling is not available</h2>
          <p className="text-base text-neutral-700 mt-2">
            Your workforce identity is approved. An administrator must activate the secure communications edge before calls can be placed.
          </p>
        </section>
      ) : !data.agent && !nativeMobile ? (
        <div className="grid gap-4 desktop:grid-cols-[minmax(260px,360px)_1fr]">
          <section className="border border-neutral-300 bg-neutral-100 p-4" aria-labelledby="identity-ready-title">
            <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Workforce communications</p>
            <h2 id="identity-ready-title" className="text-h4 mt-1">Identity approved</h2>
            <p className="text-base text-neutral-700 mt-2">
              Directory access is active, but this account has no calling extension. Ask an administrator to reprovision the workforce identity.
            </p>
            {data.communications.access.veritasId && <p className="text-sm font-semibold mt-3">
              VERITAS ID <span className="font-mono break-all">{data.communications.access.veritasId}</span>
            </p>}
          </section>
          <section className="border border-neutral-300 bg-bg p-4">
            <CommunicationDirectory data={data.communications} phoneState={visiblePhoneState}
              peerSupported={peer.supported}
              busy={busy} onCall={(entry) => void callDirectoryEntry(entry)} />
          </section>
        </div>
      ) : (
        <div className="space-y-4">
          {data.transcription.enabled && !nativeMobile && <div className="call-center-captions">
            <CaptionPanel
              segments={captions.segments}
              status={captions.status}
              error={captions.error}
              retentionDays={data.transcription.retentionDays}
              active={phone.state === 'connected' || phone.state === 'held'}
              paused={captionsPaused}
              onTogglePause={() => setCaptionsPaused((current) => !current)}
            />
          </div>}

        {nativeMobile && simpleMobile && nativeTab === 'directory' && <section
          className="border border-neutral-300 bg-bg p-4">
          <CommunicationDirectory data={data.communications} phoneState={visiblePhoneState}
            peerSupported={peer.supported}
            busy={busy} simple onCall={(entry) => void callDirectoryEntry(entry)}
            onCallContact={(card) => void callSecureContact(card)} />
        </section>}

        {nativeMobile && simpleMobile && nativeTab === 'messages' && <section
          className="border border-neutral-300 bg-bg p-4">
          <SecureMessaging directory={data.communications.directory} />
        </section>}

          {nativeMobile && simpleMobile && nativeTab === 'recents' && <section
          className="border border-neutral-300 bg-bg p-4">
          <HistoryPanel calls={data.recentCalls} onRefresh={onRefresh} allowTranscripts={false} />
        </section>}

        {nativeMobile && (nativeTab === 'wellness' || wellnessOpened) && <section
          hidden={!simpleMobile || nativeTab !== 'wellness'}
          className="border border-neutral-300 bg-bg p-4">
          <FieldWellnessScreen />
        </section>}

        {nativeMobile && simpleMobile && nativeTab === 'settings' && <NativePhoneSettings
          networkPath={networkPath} safetyLocation={safetyLocation}
          advancedAvailable={Boolean(data.agent && advancedMobileAvailable)}
          onOpenAudio={() => { setNativeTab('phone'); setAudioDevicesOpen(true); }}
          onShowAdvanced={() => { setShowAdvancedMobile(true); setNativeTab('phone'); }} />}

        {(!nativeMobile || !simpleMobile || nativeTab === 'phone') && <div className="grid desktop:grid-cols-[minmax(320px,420px)_1fr] gap-5 items-start">
          <div className="space-y-5 min-w-0">
          <section className="call-center-phone border border-neutral-300 bg-neutral-100 p-4" aria-labelledby="softphone-title">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id="softphone-title" className="text-h4">VERITAS phone</h2>
                <p className="text-sm text-neutral-700">
                  {simpleMobile || !data.agent
                    ? 'VERITAS ID active' : `Extension ${data.agent.extension} · ${data.agent.status}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button"
                  className={`btn px-3 ${audioDevicesOpen ? 'btn-primary' : 'btn-secondary'}`}
                  aria-label={audioDevicesOpen ? 'Close audio devices' : 'Open audio devices'}
                  aria-expanded={audioDevicesOpen} aria-controls="call-center-audio-devices"
                  title={audioDevicesOpen ? 'Close audio devices' : 'Choose microphone and speaker'}
                  onClick={() => setAudioDevicesOpen((current) => !current)}>
                  <SpeakerWaveIcon className="h-5 w-5" aria-hidden />
                </button>
                {!data.agent ? <span className="tag tag-neutral">Peer ready</span>
                  : phone.state === 'offline' || phone.state === 'failed' ? (
                  <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void connect()}>
                    <SignalIcon className="h-5 w-5" aria-hidden /> Connect
                  </button>
                ) : (
                  <button type="button" className="btn btn-secondary" disabled={busy || callActive}
                    onClick={() => void disconnect()}>
                    <SignalSlashIcon className="h-5 w-5" aria-hidden /> Disconnect
                  </button>
                )}
              </div>
            </div>

            {data.agent && phone.state !== 'offline' && phone.state !== 'failed' && !simpleMobile
              && <div className="grid grid-cols-2 gap-2 mt-3" aria-label="Agent status">
              <button type="button" className={`btn ${data.agent.status === 'available' ? 'btn-primary' : 'btn-secondary'}`}
                disabled={busy} onClick={() => void changeAgentStatus('available')}>
                <CheckCircleIcon className="h-5 w-5" aria-hidden /> Available
              </button>
              <button type="button" className={`btn ${data.agent.status === 'break' ? 'bg-presence-idle text-white' : 'btn-secondary'}`}
                disabled={busy || callActive} onClick={() => void changeAgentStatus('break')}>
                <ClockIcon className="h-5 w-5" aria-hidden /> Break
              </button>
            </div>}

            {(phone.state === 'connected' || phone.state === 'held') && <RemoteSoundMeter
              level={phone.outputLevel}
              soundDetected={phone.remoteSoundDetected}
              held={phone.state === 'held'}
              mediaPath={phone.mediaPath}
            />}

            {audioDevicesOpen && <AudioDevicePanel
              inputs={phone.audioInputs}
              outputs={phone.audioOutputs}
              inputDeviceId={phone.inputDeviceId}
              outputDeviceId={phone.outputDeviceId}
              inputLevel={phone.inputLevel}
              outputLevel={phone.outputLevel}
              outputSelectionSupported={phone.outputSelectionSupported}
              callActive={callActive}
              audioTest={phone.audioTest}
              mediaPath={phone.mediaPath}
              onInputChange={phone.selectInputDevice}
              onOutputChange={(deviceId) => { void phone.selectOutputDevice(deviceId).catch(() => undefined); }}
              onTestMicrophone={() => { void phone.testMicrophone(); }}
              onTestSpeaker={() => { void phone.testSpeaker(); }}
              onClose={() => setAudioDevicesOpen(false)}
            />}

            {contact && <div className="mt-4 border border-accent-300 bg-accent-100 p-3 flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Selected person</p>
                <p className="font-semibold truncate">{contact.name}</p>
                <p className="text-sm text-neutral-700 tabular-nums">{operatorNumber(contact.phone)}</p>
              </div>
              {!travelMode && !simpleMobile && onScheduleVisit && <button type="button" className="btn btn-secondary"
                onClick={() => onScheduleVisit(contact.id)}>
                <CalendarDaysIcon className="h-5 w-5" aria-hidden /> Schedule visit
              </button>}
            </div>}

            {peerActive && <div className="mt-4 border border-accent-300 bg-accent-100 p-4 text-center">
              <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Direct peer call</p>
              <p className="mt-1 font-mono text-xs break-all">{peer.remoteVeritasId}</p>
              <p className="mt-2 text-xs text-neutral-700">No employee name or extension is shown during this call.</p>
            </div>}

            {data.agent && !peerActive && (!travelMode || callActive) && <div className="call-center-dialer">
            <label className="field mt-4" htmlFor="dial-number"><span>US phone number</span></label>
            <div className="flex gap-2">
              <input id="dial-number" className="input text-h4 tabular-nums" inputMode="tel"
                placeholder="Area code and phone number" aria-describedby="dial-number-help"
                autoComplete="tel-national" value={number} maxLength={18}
                onChange={(event) => {
                  const next = normalizePhone(event.target.value);
                  if (next.length === number.length + 1 && next.startsWith(number)) {
                    phone.playKeyTone(next.slice(-1));
                  }
                  setNumber(next);
                  if (contact && next !== normalizePhone(contact.phone)) setContact(null);
                  markActivity('keyboard_command');
                }}
                disabled={callActive || busy} />
              <button type="button" className="btn btn-secondary px-3" aria-label="Delete last digit"
                disabled={!number || callActive || busy} onClick={() => {
                  setNumber((value) => value.slice(0, -1)); setContact(null);
                }}>
                <BackspaceIcon className="h-5 w-5" aria-hidden />
              </button>
            </div>

            <p id="dial-number-help" className="text-xs text-neutral-700 mt-1">
              Enter 10 digits. US carrier formatting is handled automatically.
            </p>

            <button type="button" className="btn btn-secondary w-full mt-3 desktop:hidden"
              aria-expanded={keypadOpen} aria-controls="mobile-dial-pad"
              onClick={() => setKeypadOpen((current) => !current)}>
              {keypadOpen ? 'Hide keypad' : 'Show keypad'}
            </button>

            <div id="mobile-dial-pad"
              className={`${keypadOpen ? 'grid' : 'hidden'} desktop:grid grid-cols-3 gap-2 my-4`}
              aria-label="Dial pad">
              {DIAL_KEYS.map(([digit, letters]) => <button key={digit} type="button"
                className="min-h-[52px] border border-neutral-300 bg-bg hover:bg-accent-100 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-accent-600"
                disabled={busy || (!callActive && (digit === '*' || digit === '#'))}
                onClick={() => {
                  if (callActive) void phone.sendTone(digit);
                  else {
                    phone.playKeyTone(digit);
                    setNumber((value) => `${value}${digit}`.slice(0, 10));
                    setContact(null);
                  }
                }}>
                <span className="block text-h4 leading-none">{digit}</span>
                {letters && <span className="block text-[10px] tracking-[.12em] text-neutral-700 mt-1">{letters}</span>}
              </button>)}
            </div>
            </div>}

            {travelMode && !contact && !callActive && <p className="my-4 border border-neutral-300 bg-bg p-3 text-sm">
              Choose a person from People or Visits before entering travel mode.
            </p>}

            {peer.state === 'ringing' && <div className="grid grid-cols-2 gap-2 my-3">
              <button type="button" className="btn btn-primary" onClick={() => void peer.answer()}>
                <PhoneArrowDownLeftIcon className="h-5 w-5" aria-hidden /> Answer
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => void peer.decline()}>
                <PhoneXMarkIcon className="h-5 w-5" aria-hidden /> Decline
              </button>
            </div>}

            {phone.state === 'ringing' && !peerActive && <div className="grid grid-cols-2 gap-2 mb-3">
              <button type="button" className="btn btn-primary" onClick={() => {
                phone.prepareCallMedia();
                void phone.answer().catch(() => phone.cancelPreparedMedia());
              }}>
                <PhoneArrowDownLeftIcon className="h-5 w-5" aria-hidden /> Answer
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => void phone.decline()}>
                <PhoneXMarkIcon className="h-5 w-5" aria-hidden /> Decline
              </button>
            </div>}

            {!data.agent && !peerActive ? (
              <p className="my-4 border border-neutral-300 bg-bg p-3 text-center text-sm text-neutral-700">
                Choose a contact from the Directory to place a protected call.
              </p>
            ) : !callActive && phone.state !== 'ringing' && peer.state !== 'ringing' ? (
              <button type="button" className="call-center-primary-action btn btn-primary w-full" disabled={!canDial || busy}
                onClick={() => void placeCall()}><PhoneIcon className="h-5 w-5" aria-hidden /> Call</button>
            ) : peerActive && peer.state !== 'ringing' ? (
              <div className="call-center-call-controls">
                <button type="button" className="btn bg-alert-600 text-white hover:bg-alert-700 w-full"
                  disabled={busy} onClick={() => void endCurrentCall()}>
                  <PhoneXMarkIcon className="h-5 w-5" aria-hidden /> End protected call
                </button>
              </div>
            ) : callActive ? (
              <div className="call-center-call-controls grid grid-cols-2 gap-2">
                <button type="button" className="btn btn-secondary" onClick={phone.toggleMute}>
                  <MicrophoneIcon className="h-5 w-5" aria-hidden /> {phone.muted ? 'Unmute' : 'Mute'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => void phone.toggleHold()}>
                  <PauseIcon className="h-5 w-5" aria-hidden /> {phone.state === 'held' ? 'Resume' : 'Hold'}
                </button>
                <button type="button" className="btn bg-alert-600 text-white hover:bg-alert-700" disabled={busy}
                  onClick={() => void endCurrentCall()}><PhoneXMarkIcon className="h-5 w-5" aria-hidden /> End</button>
                <button type="button" className="btn btn-primary" disabled={busy
                    || !data.escalationQueue || data.escalationQueue.availableRecipients < 1}
                  title={!data.escalationQueue ? 'Manager queue is not configured'
                    : data.escalationQueue.availableRecipients < 1 ? 'No manager or team lead is available'
                      : 'Send this call to the manager queue'}
                  onClick={() => setEscalationOpen(true)}>
                  <UserGroupIcon className="h-5 w-5" aria-hidden /> Escalate
                </button>
              </div>
            ) : null}

            {(message || peer.error || phone.error) && <p className="field-error mt-3" role="alert">
              {message ?? peer.error ?? phone.error}
            </p>}
            {elapsed && <p className="text-sm text-neutral-700 mt-3">{elapsed}</p>}
          </section>

          {wrapUpCallId && <section className="border border-accent-300 bg-accent-100 p-4" aria-labelledby="wrap-up-title">
            <h2 id="wrap-up-title" className="text-h4">Call wrap-up</h2>
            <label className="field mt-3" htmlFor="call-disposition"><span>Outcome</span></label>
            <select id="call-disposition" className="input" value={disposition}
              onChange={(event) => setDisposition(event.target.value)}>
              <option value="reached">Reached</option><option value="no_answer">No answer</option>
              <option value="voicemail">Voicemail</option><option value="wrong_number">Wrong number</option>
              <option value="call_back">Call back</option><option value="other">Other</option>
            </select>
            <label className="field mt-3" htmlFor="call-note"><span>Protected note</span></label>
            <textarea id="call-note" className="input min-h-[90px]" maxLength={4000}
              value={callNote} onChange={(event) => setCallNote(event.target.value)} />
            <button type="button" className="btn btn-primary w-full mt-3" disabled={busy}
              onClick={() => void saveWrapUp()}>
              <DocumentCheckIcon className="h-5 w-5" aria-hidden /> Save call
            </button>
          </section>}
          </div>

          {!travelMode && !simpleMobile && <OperationsWorkspace activeTab={workspaceTab} onChange={(tab) => {
            setWorkspaceTab(tab);
            markActivity('operator_action');
          }}
            segments={captions.segments} callActive={callActive} data={data}
            onRefresh={onRefresh} onEditPlaybook={onEditPlaybook}
            phoneState={visiblePhoneState} peerSupported={peer.supported} busy={busy}
            onCallDirectory={(entry) => void callDirectoryEntry(entry)} />}
        </div>}
        </div>
      )}

      <audio ref={phone.remoteAudioRef} autoPlay playsInline aria-label="Call audio" />
      <audio ref={peer.remoteAudioRef} autoPlay playsInline aria-label="Protected peer call audio" />

      {nativeMobile && data.communications.access.status === 'approved'
        && <NativePhoneNavigation active={nativeTab} onChange={(tab) => {
          setShowAdvancedMobile(false);
          setNativeTab(tab);
          markActivity('operator_action');
        }} />}

      <Sheet open={escalationOpen} title="Send call to managers"
        onClose={() => { if (!busy) setEscalationOpen(false); }}
        actions={<>
          <button type="button" className="btn btn-secondary flex-1" disabled={busy}
            onClick={() => setEscalationOpen(false)}>Cancel</button>
          <button type="button" className="btn btn-primary flex-1" disabled={busy || !activeCallId}
            onClick={() => void requestEscalation()}>
            {busy ? 'Sending…' : 'Send to manager queue'}
          </button>
        </>}>
        <p className="text-base text-neutral-700">
          The caller will move to {data.escalationQueue?.name ?? 'the manager queue'}.
          Available recipients: {data.escalationQueue?.availableRecipients ?? 0}.
        </p>
        <label className="field mt-4" htmlFor="escalation-reason"><span>Reason</span></label>
        <select id="escalation-reason" className="input" data-initial-focus
          value={escalationReason} onChange={(event) => setEscalationReason(event.target.value)}>
          <option value="supervisor_support">Supervisor support</option>
          <option value="customer_request">Caller asked for a manager</option>
          <option value="complex_issue">Complex issue</option>
          <option value="complaint">Complaint</option>
          <option value="safety">Safety concern</option>
          <option value="other">Other</option>
        </select>
        <label className="field mt-4" htmlFor="escalation-detail">
          <span>Short handoff note, optional</span>
        </label>
        <textarea id="escalation-detail" className="input min-h-[96px]" maxLength={500}
          value={escalationDetail} onChange={(event) => setEscalationDetail(event.target.value)}
          placeholder="What should the next person know?" />
        <p className="text-xs text-neutral-700 mt-2">
          The reason and transfer result are recorded in the protected audit trail.
        </p>
      </Sheet>
    </div>
  );
}

function NativeSecurityStatus({ data, phoneState, peerState, peerRemoteVeritasId }: {
  data: CallCenterData;
  phoneState: PhoneState;
  peerState: PeerCallState;
  peerRemoteVeritasId: string | null;
}) {
  const active = ['calling', 'connected', 'held'].includes(phoneState);
  const internal = ['calling', 'ringing', 'connected'].includes(peerState);
  const internalReady = data.communications.internalCalling === 'enabled';
  const failed = peerState === 'failed' || (internal && !internalReady);
  const title = failed ? 'Protected communication interrupted'
    : peerState === 'connected' ? 'Direct protected call'
      : peerState === 'ringing' ? 'Protected incoming call'
        : peerState === 'calling' ? 'Calling VERITAS ID'
      : active ? 'Carrier call protected to VERITAS edge'
        : internalReady ? 'VERITAS peer calling ready' : 'Internal calling held';
  const detail = failed ? 'The fail-closed policy ended the protected path. Reopen the contact and try again.'
    : internal ? `PQE2EE signaling and AES-256-GCM audio are active${peerRemoteVeritasId
      ? ` for ${peerRemoteVeritasId}` : ''}. TURN can relay ciphertext when a direct route is unavailable.`
      : active ? 'The public carrier controls protection beyond the VERITAS edge.'
        : internalReady ? 'VERITAS ID calls bypass SIP and use enrolled device keys.'
          : 'An administrator must restore the protected media service.';
  return <div className={`mb-4 flex items-center gap-3 border p-3 ${failed
    ? 'border-alert-400 bg-alert-100' : 'border-presence-active bg-neutral-100'}`}
    role="status" aria-live="polite">
    {failed ? <SignalSlashIcon className="h-6 w-6 shrink-0 text-alert-700" aria-hidden />
      : <SignalIcon className="h-6 w-6 shrink-0 text-presence-active" aria-hidden />}
    <span className="min-w-0">
      <strong className="block">{title}</strong>
      <span className="block text-xs text-neutral-700">{detail}</span>
    </span>
  </div>;
}

function NativePhoneSettings({ networkPath, safetyLocation, advancedAvailable,
  onOpenAudio, onShowAdvanced }: {
  networkPath: NetworkPathState;
  safetyLocation: SafetyLocationState;
  advancedAvailable: boolean;
  onOpenAudio: () => void;
  onShowAdvanced: () => void;
}) {
  const locationLabel: Record<SafetyLocationState['status'], string> = {
    inactive: 'Off', starting: 'Starting', sharing: 'Sharing',
    permission_required: 'Permission needed', unavailable: 'Not supported', error: 'Needs attention',
  };
  return <section className="border border-neutral-300 bg-bg p-4" aria-labelledby="native-settings-title">
    <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Device</p>
    <h2 id="native-settings-title" className="text-h3 mt-1">Settings</h2>
    <button type="button" className="btn btn-secondary mt-5 w-full justify-start" onClick={onOpenAudio}>
      <SpeakerWaveIcon className="h-5 w-5" aria-hidden /> Microphone and speaker
    </button>
    <dl className="mt-4 divide-y divide-neutral-300 border-y border-neutral-300 text-sm">
      <div className="flex justify-between gap-4 py-3"><dt>Network</dt><dd className="font-semibold text-right">{networkPath.label}</dd></div>
      <div className="flex justify-between gap-4 py-3"><dt>Safety location</dt><dd className="font-semibold text-right">{locationLabel[safetyLocation.status]}</dd></div>
      {safetyLocation.accuracy !== null && <div className="flex justify-between gap-4 py-3">
        <dt>Location accuracy</dt><dd className="font-semibold text-right">±{Math.round(safetyLocation.accuracy)} m</dd>
      </div>}
    </dl>
    {safetyLocation.status === 'permission_required' && <p className="form-error mt-3" role="alert">
      Allow location while using VERITAS in Android App info, then reopen the app.
    </p>}
    <p className="mt-3 text-xs text-neutral-700">
      Safety position updates while this app is open. Coordinates are encrypted and available only to authorized VERITAS personnel.
    </p>
    {advancedAvailable && <button type="button" className="btn btn-secondary mt-6 w-full"
      onClick={onShowAdvanced}>
      <AdjustmentsHorizontalIcon className="h-5 w-5" aria-hidden /> Administrator controls
    </button>}
  </section>;
}

function NativePhoneNavigation({ active, onChange }: {
  active: NativePhoneTab; onChange: (tab: NativePhoneTab) => void;
}) {
  const items: Array<{ id: NativePhoneTab; label: string; Icon: typeof PhoneIcon }> = [
    { id: 'phone', label: 'Phone', Icon: PhoneIcon },
    { id: 'messages', label: 'Messages', Icon: ChatBubbleBottomCenterTextIcon },
    { id: 'directory', label: 'Directory', Icon: UserGroupIcon },
    { id: 'recents', label: 'Recents', Icon: ClockIcon },
    { id: 'wellness', label: 'Wellness', Icon: DocumentCheckIcon },
    { id: 'settings', label: 'Settings', Icon: AdjustmentsHorizontalIcon },
  ];
  return <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-300 bg-bg
    pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_12px_rgba(0,0,0,.08)]"
    aria-label="Field app">
    <div className="mx-auto grid max-w-[720px] grid-cols-6">
      {items.map(({ id, label, Icon }) => <button key={id} type="button"
        className={`min-h-[64px] px-1 py-2 text-[11px] font-semibold ${active === id
          ? 'bg-accent-100 text-accent-800' : 'text-neutral-700'}`}
        aria-current={active === id ? 'page' : undefined} onClick={() => onChange(id)}>
        <Icon className="mx-auto mb-1 h-5 w-5" aria-hidden />
        <span className="block truncate">{label}</span>
      </button>)}
    </div>
  </nav>;
}

export function NetworkPathIndicator({ value }: { value: NetworkPathState }) {
  const active = value.connected && value.validated;
  return <div className="flex min-h-touch items-center gap-2 rounded-full border border-neutral-300 bg-neutral-100 px-3"
    role="status" aria-label={`Network: ${value.label}. ${value.detail}`}>
    {active
      ? <SignalIcon className="h-5 w-5 text-presence-active" aria-hidden />
      : <SignalSlashIcon className="h-5 w-5 text-presence-offline" aria-hidden />}
    <span className="min-w-0">
      <span className="block truncate text-sm font-semibold">{value.label}</span>
      <span className="block truncate text-[11px] text-neutral-700">
        {value.detail}{value.verified ? ' · verified' : ''}
      </span>
    </span>
  </div>;
}

function MobileInstallControl() {
  const [prompt, setPrompt] = useState<DeferredInstallPrompt | null>(null);
  const [installed, setInstalled] = useState(appRunsStandalone);
  const [showHelp, setShowHelp] = useState(false);
  const nativeMobile = isNativeMobile();

  useEffect(() => {
    const installAvailable = (event: Event) => {
      event.preventDefault();
      setPrompt(event as DeferredInstallPrompt);
    };
    const installationCompleted = () => {
      setInstalled(true);
      setPrompt(null);
      setShowHelp(false);
    };
    window.addEventListener('beforeinstallprompt', installAvailable);
    window.addEventListener('appinstalled', installationCompleted);
    return () => {
      window.removeEventListener('beforeinstallprompt', installAvailable);
      window.removeEventListener('appinstalled', installationCompleted);
    };
  }, []);

  if (nativeMobile) return <div className="call-center-install desktop:hidden" role="status">
    <DevicePhoneMobileIcon className="h-5 w-5 text-presence-active" aria-hidden />
    <span>
      <strong>Private mobile app</strong>
      <span className="block text-xs text-neutral-700">
        Delayed snapshots use signed PQE2EE envelopes. Calls remain live.
      </span>
    </span>
  </div>;

  async function install() {
    if (!prompt) {
      setShowHelp((current) => !current);
      return;
    }
    await prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice.outcome === 'accepted') setInstalled(true);
    setPrompt(null);
  }

  if (installed) return <div className="call-center-install desktop:hidden" role="status">
    <DevicePhoneMobileIcon className="h-5 w-5 text-presence-active" aria-hidden />
    <span><strong>Mobile app installed</strong><span className="block text-xs text-neutral-700">Opens directly to the secure phone.</span></span>
  </div>;

  return <section className="call-center-install desktop:hidden" aria-labelledby="install-call-center-heading">
    <DevicePhoneMobileIcon className="h-6 w-6 shrink-0 text-accent-700" aria-hidden />
    <div className="min-w-0 flex-1">
      <h2 id="install-call-center-heading" className="text-base">Install the mobile call center</h2>
      <p className="mt-1 text-xs text-neutral-700">
        Adds Veritas Calls to the home screen. Protected records and transcripts are not cached on this device.
      </p>
      {showHelp && <p className="mt-2 border-l-2 border-accent-400 pl-2 text-xs" role="status">
        Vanadium: open the browser menu, choose Add to Home screen, then confirm Install. Allow microphone access only while using the app.
      </p>}
    </div>
    <button type="button" className="btn btn-secondary shrink-0 px-3" onClick={() => void install()}>
      <ArrowDownTrayIcon className="h-5 w-5" aria-hidden /> {prompt ? 'Install' : 'How to install'}
    </button>
  </section>;
}

function appRunsStandalone() {
  if (typeof window === 'undefined') return false;
  const iosNavigator = navigator as Navigator & { standalone?: boolean };
  return Boolean(iosNavigator.standalone)
    || (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches);
}

function OperatorSummary({ data, now, onReturnAvailable, busy }: {
  data: CallCenterData;
  now: number;
  onReturnAvailable: () => void;
  busy: boolean;
}) {
  const agent = data.agent!;
  const stateStartedAt = new Date(agent.stateStartedAt).getTime();
  const duration = Number.isFinite(stateStartedAt)
    ? Math.max(agent.stateDurationSeconds, Math.floor((now - stateStartedAt) / 1_000))
    : agent.stateDurationSeconds;
  const next = data.schedule.nextEvent;
  const liveState = ['ringing', 'on_call', 'after_call_work'].includes(agent.status);
  const telemetryDelayed = liveState && data.telemetry.lagSeconds !== null
    && data.telemetry.lagSeconds > data.telemetry.toleranceSeconds;

  return <section className="mb-4" aria-labelledby="operator-day-heading">
    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
      <div>
        <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Operator workspace</p>
        <h2 id="operator-day-heading" className="text-h4">Today at a glance</h2>
      </div>
      <span className="tag tag-neutral">{data.dailyProgress.status} data</span>
    </div>
    <div className="grid grid-cols-1 min-[560px]:grid-cols-2 desktop:grid-cols-4 gap-2">
      <article className={`border p-3 ${agent.status === 'auto_break'
        ? 'border-alert-400 bg-alert-100' : 'border-neutral-300 bg-neutral-100'}`}>
        <div className="flex items-center gap-2">
          <ClockIcon className="h-5 w-5 text-accent-700" aria-hidden />
          <h3 className="text-base font-semibold">Current state</h3>
        </div>
        <p className="font-heading text-h4 mt-2">{PRESENCE_LABEL[agent.status]}</p>
        <p className="text-sm text-neutral-700 tabular-nums">{formatDuration(duration)}</p>
        {agent.status === 'auto_break' && <>
          <p className="text-sm mt-2">Five minutes passed without qualifying activity. Active calls and approved work are excluded.</p>
          <button type="button" className="btn btn-secondary w-full mt-2"
            disabled={busy} onClick={onReturnAvailable}>Return available</button>
        </>}
      </article>

      <article className="border border-neutral-300 bg-neutral-100 p-3">
        <div className="flex items-center gap-2">
          <CalendarDaysIcon className="h-5 w-5 text-accent-700" aria-hidden />
          <h3 className="text-base font-semibold">Next schedule event</h3>
        </div>
        {next ? <>
          <p className="font-heading text-h4 mt-2 capitalize">{next.type.replace('_', ' ')}</p>
          <p className="text-sm text-neutral-700">{formatScheduleRange(next, data.schedule.timezone)}</p>
        </> : <p className="text-sm text-neutral-700 mt-2">No upcoming event is assigned.</p>}
      </article>

      <article className="border border-neutral-300 bg-neutral-100 p-3">
        <div className="flex items-center gap-2">
          <SpeakerWaveIcon className="h-5 w-5 text-accent-700" aria-hidden />
          <h3 className="text-base font-semibold">Queue assignments</h3>
        </div>
        <p className="font-heading text-h4 mt-2 tabular-nums">{data.queues.length}</p>
        <p className="text-sm text-neutral-700 line-clamp-2">
          {data.queues.length ? data.queues.map((queue) => queue.name).join(', ') : 'No queue assigned'}
        </p>
      </article>

      <article className="border border-neutral-300 bg-neutral-100 p-3">
        <div className="flex items-center gap-2">
          <ChartBarSquareIcon className="h-5 w-5 text-accent-700" aria-hidden />
          <h3 className="text-base font-semibold">Daily progress</h3>
        </div>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-sm">
          <div><dt className="text-neutral-700">Handled</dt><dd className="font-semibold tabular-nums">{data.dailyProgress.callsHandled}</dd></div>
          <div><dt className="text-neutral-700">Appointments</dt><dd className="font-semibold tabular-nums">{data.dailyProgress.appointmentsCreated}</dd></div>
          <div><dt className="text-neutral-700">Answered</dt><dd className="font-semibold tabular-nums">{data.dailyProgress.callsAnswered}</dd></div>
          <div><dt className="text-neutral-700">Talk time</dt><dd className="font-semibold tabular-nums">{formatDuration(data.dailyProgress.talkSeconds)}</dd></div>
        </dl>
      </article>
    </div>
    {telemetryDelayed && <p className="form-error mt-2" role="status">
      Call updates are delayed by {data.telemetry.lagSeconds} seconds. Controls remain available, but recent status may be incomplete.
    </p>}
  </section>;
}

function AudioDevicePanel({
  inputs, outputs, inputDeviceId, outputDeviceId, inputLevel, outputLevel,
  outputSelectionSupported, callActive, audioTest, mediaPath, onInputChange, onOutputChange,
  onTestMicrophone, onTestSpeaker, onClose,
}: {
  inputs: Array<{ deviceId: string; label: string }>;
  outputs: Array<{ deviceId: string; label: string }>;
  inputDeviceId: string;
  outputDeviceId: string;
  inputLevel: number;
  outputLevel: number;
  outputSelectionSupported: boolean;
  callActive: boolean;
  audioTest: 'idle' | 'microphone' | 'speaker';
  mediaPath: import('../hooks/useSipPhone.js').MediaPathEvidence;
  onInputChange: (deviceId: string) => void;
  onOutputChange: (deviceId: string) => void;
  onTestMicrophone: () => void;
  onTestSpeaker: () => void;
  onClose: () => void;
}) {
  const microphone = inputs.find((device) => device.deviceId === inputDeviceId)?.label
    ?? 'System default microphone';
  const speaker = outputs.find((device) => device.deviceId === outputDeviceId)?.label
    ?? 'System default speaker';
  return <section id="call-center-audio-devices" className="mt-3 border border-neutral-300 bg-bg p-3"
    aria-labelledby="audio-devices-title">
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-start gap-2 min-w-0">
        <AdjustmentsHorizontalIcon className="h-5 w-5 shrink-0 text-accent-700 mt-0.5" aria-hidden />
        <div className="min-w-0">
          <h3 id="audio-devices-title" className="font-heading font-semibold">Audio devices</h3>
          <p className="text-xs text-neutral-700">Choose where you speak and hear the call.</p>
        </div>
      </div>
      <button type="button" className="btn btn-ghost p-2 shrink-0" aria-label="Close audio devices"
        onClick={onClose}>
        <XMarkIcon className="h-5 w-5" aria-hidden />
      </button>
    </div>

    <div className="grid gap-3 mt-3">
      <div className="min-w-0">
        <label htmlFor="call-center-microphone"
          className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[.06em] text-neutral-700">
          <MicrophoneIcon className="h-4 w-4" aria-hidden /> Microphone
        </label>
        <select id="call-center-microphone" className="input mt-1 w-full truncate"
          value={inputDeviceId} disabled={callActive}
          aria-describedby="call-center-microphone-status"
          onChange={(event) => onInputChange(event.target.value)}>
          {inputs.length === 0 && <option value="">System default microphone</option>}
          {inputs.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label}</option>)}
        </select>
        <span id="call-center-microphone-status" className="block text-xs text-neutral-700 mt-1 break-words">
          <strong>{callActive ? 'In use:' : 'Selected:'}</strong> {microphone}
        </span>
      </div>

      <div className="min-w-0">
        <label htmlFor="call-center-speaker"
          className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[.06em] text-neutral-700">
          <SpeakerWaveIcon className="h-4 w-4" aria-hidden /> Speaker
        </label>
        <select id="call-center-speaker" className="input mt-1 w-full truncate"
          value={outputDeviceId} disabled={!outputSelectionSupported}
          aria-describedby="call-center-speaker-status"
          onChange={(event) => onOutputChange(event.target.value)}>
          {outputs.length === 0 && <option value="">System default speaker</option>}
          {outputs.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label}</option>)}
        </select>
        <span id="call-center-speaker-status" className="block text-xs text-neutral-700 mt-1 break-words">
          <strong>{callActive ? 'In use:' : 'Selected:'}</strong> {speaker}
          {!outputSelectionSupported && ' · Controlled by macOS Sound settings'}
        </span>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3 mt-3" aria-label="Live audio levels">
      <AudioLevel label="Mic activity" value={inputLevel} />
      <AudioLevel label="Call audio" value={outputLevel} />
    </div>

    <div className="mt-3 border border-neutral-300 bg-neutral-100 p-2 text-xs" role="status">
      <strong>Media path:</strong> {mediaPathLabel(mediaPath.state)}
      {callActive && <span className="block mt-1 tabular-nums">
        Incoming {mediaPath.packetsReceived} packets · Outgoing {mediaPath.packetsSent} packets
      </span>}
      {mediaPath.localCandidateType && <span className="block mt-1">
        Route {mediaPath.localCandidateType} to {mediaPath.remoteCandidateType || 'peer'}
        {mediaPath.relayProtocol ? ` over ${mediaPath.relayProtocol.toUpperCase()}` : ''}
      </span>}
    </div>

    <div className="grid grid-cols-2 gap-2 mt-3">
      <button type="button" className="btn btn-secondary px-2" disabled={callActive || audioTest !== 'idle'}
        onClick={onTestMicrophone}>
        <MicrophoneIcon className="h-5 w-5" aria-hidden />
        {audioTest === 'microphone' ? 'Speak now' : 'Test mic'}
      </button>
      <button type="button" className="btn btn-secondary px-2" disabled={audioTest !== 'idle'}
        onClick={onTestSpeaker}>
        <PlayIcon className="h-5 w-5" aria-hidden />
        {audioTest === 'speaker' ? 'Playing' : 'Test speaker'}
      </button>
    </div>
  </section>;
}

function AudioLevel({ label, value }: { label: string; value: number }) {
  return <div className="min-w-0">
    <div className="flex items-center justify-between gap-2 text-xs text-neutral-700">
      <span>{label}</span><span>{value > 2 ? 'Active' : 'Quiet'}</span>
    </div>
    <div className="h-2 mt-1 overflow-hidden rounded-full bg-neutral-200" role="meter"
      aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={value}>
      <span className={`block h-full rounded-full transition-[width] duration-100 ${
        value > 2 ? 'bg-presence-active' : 'bg-neutral-400'
      }`} style={{ width: `${Math.max(3, value)}%` }} />
    </div>
  </div>;
}

export function RemoteSoundMeter({
  level, soundDetected, held, mediaPath,
}: {
  level: number;
  soundDetected: boolean;
  held: boolean;
  mediaPath: import('../hooks/useSipPhone.js').MediaPathEvidence;
}) {
  const receivingMedia = mediaPath.packetsReceived > 0 || mediaPath.state === 'active';
  const status = held
    ? 'Call on hold'
    : soundDetected
      ? 'Sound detected'
      : receivingMedia
        ? 'Connected, listening'
        : 'Connecting audio';
  const meterWidth = held ? 0 : Math.max(soundDetected ? 8 : 2, level);

  return <section className="mt-3 border border-neutral-300 bg-bg p-3"
    aria-labelledby="remote-sound-title">
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <SpeakerWaveIcon className="h-5 w-5 shrink-0 text-accent-700" aria-hidden />
        <h3 id="remote-sound-title" className="font-heading font-semibold">Other person</h3>
      </div>
      <span className="flex items-center gap-2 text-sm font-semibold" aria-live="polite">
        <span className={`h-2.5 w-2.5 rounded-full ${
          soundDetected && !held ? 'bg-presence-active' : 'bg-neutral-400'
        }`} aria-hidden />
        {status}
      </span>
    </div>
    <div className="h-4 mt-2 overflow-hidden rounded-full bg-neutral-200" role="meter"
      aria-label="Sound from the other person" aria-valuemin={0} aria-valuemax={100}
      aria-valuenow={Math.round(level)} aria-valuetext={status}>
      <span className={`block h-full rounded-full transition-[width] duration-100 ${
        soundDetected && !held ? 'bg-presence-active' : 'bg-accent-500'
      }`} style={{ width: `${Math.min(100, meterWidth)}%` }} />
    </div>
    <div className="flex justify-between mt-1 text-[.7rem] text-neutral-600" aria-hidden>
      <span>Quiet</span><span>Loud</span>
    </div>
    <p className="mt-1 text-xs text-neutral-700">
      Sensitive to quiet speech and background sound. This meter does not record audio.
    </p>
  </section>;
}

function mediaPathLabel(state: import('../hooks/useSipPhone.js').MediaPathState) {
  return {
    idle: 'Waiting for a call',
    armed: 'Speaker unlocked',
    negotiating: 'Connecting secure audio',
    active: 'Two-way audio packets active',
    blocked: 'Speaker playback blocked',
    no_media: 'No two-way audio packets detected',
  }[state];
}

function OperationsWorkspace({
  activeTab, onChange, segments, callActive, data, onRefresh, onEditPlaybook,
  phoneState, peerSupported, busy, onCallDirectory,
}: {
  activeTab: WorkspaceTab;
  onChange: (tab: WorkspaceTab) => void;
  segments: TranscriptSegment[];
  callActive: boolean;
  data: CallCenterData;
  onRefresh: () => Promise<void>;
  onEditPlaybook?: () => void;
  phoneState: PhoneState;
  peerSupported: boolean;
  busy: boolean;
  onCallDirectory: (entry: CallCenterData['communications']['directory'][number]) => void;
}) {
  const selectTab = (index: number) => {
    const tab = WORKSPACE_TABS[index];
    onChange(tab.id);
    document.getElementById(`call-center-tab-${tab.id}`)?.focus();
  };
  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      selectTab((index + 1) % WORKSPACE_TABS.length);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      selectTab((index - 1 + WORKSPACE_TABS.length) % WORKSPACE_TABS.length);
    } else if (event.key === 'Home') {
      event.preventDefault();
      selectTab(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      selectTab(WORKSPACE_TABS.length - 1);
    }
  };

  return <section className="min-w-0 border border-neutral-300 bg-bg" aria-label="Call center workspace">
    <div role="tablist" aria-label="Call center information"
      className="grid grid-cols-2 min-[720px]:grid-cols-3 desktop:grid-cols-6 border-b border-neutral-300 bg-neutral-100">
      {WORKSPACE_TABS.map((tab, index) => <button key={tab.id} type="button" role="tab"
        id={`call-center-tab-${tab.id}`} aria-selected={activeTab === tab.id}
        aria-controls={`call-center-panel-${tab.id}`} tabIndex={activeTab === tab.id ? 0 : -1}
        className={`min-h-touch px-3 py-3 text-sm font-semibold border-b-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-600 ${
          activeTab === tab.id ? 'border-accent-700 bg-bg text-accent-800' : 'border-transparent text-neutral-700 hover:bg-accent-100'
        }`}
        onClick={() => onChange(tab.id)} onKeyDown={(event) => onTabKeyDown(event, index)}>
        {tab.label}
      </button>)}
    </div>
    <div role="tabpanel" id={`call-center-panel-${activeTab}`}
      aria-labelledby={`call-center-tab-${activeTab}`} tabIndex={0}
      className="p-4 desktop:p-5 desktop:min-h-[430px] desktop:max-h-[calc(100vh-330px)] overflow-y-auto focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-600">
      {activeTab === 'directory' && <CommunicationDirectory data={data.communications}
        phoneState={phoneState} peerSupported={peerSupported} busy={busy} onCall={onCallDirectory} />}
      {activeTab === 'coach' && <ConversationCoach segments={segments} active={callActive}
        playbook={data.playbook.length > 0 ? data.playbook : CALL_PLAYBOOK}
        onEdit={onEditPlaybook} />}
      {activeTab === 'day' && <MyDayPanel data={data} />}
      {activeTab === 'security' && <SecurityPanel data={data} />}
      {activeTab === 'queues' && <QueuePanel queues={data.queues} />}
      {activeTab === 'history' && <HistoryPanel calls={data.recentCalls} onRefresh={onRefresh} />}
    </div>
  </section>;
}

type WorkforceDirectoryEntry = CallCenterData['communications']['directory'][number];

function CommunicationDirectory({ data, phoneState, peerSupported, busy, onCall, onCallContact, simple = false }: {
  data: CallCenterData['communications'];
  phoneState: PhoneState;
  peerSupported: boolean;
  busy: boolean;
  onCall: (entry: WorkforceDirectoryEntry) => void;
  onCallContact?: (card: SecureContactCard) => void;
  simple?: boolean;
}) {
  const [query, setQuery] = useState('');
  const activeCall = ['calling', 'connected', 'held', 'ringing'].includes(phoneState);
  const normalized = query.normalize('NFKC').trim().toLocaleLowerCase();
  const entries = [...(normalized ? data.directory.filter((entry) =>
    entry.displayName.toLocaleLowerCase().includes(normalized)
      || entry.veritasId.toLocaleLowerCase().includes(normalized)) : data.directory)]
    .sort((left, right) => left.displayName.localeCompare(right.displayName));
  const callsEnabled = data.internalCalling === 'enabled';

  if (simple) {
    return <NativeContactDirectory data={data} phoneState={phoneState} busy={busy}
      peerSupported={peerSupported} onCall={onCall}
      onCallContact={onCallContact ?? (() => undefined)} />;
  }

  return <section aria-labelledby="communication-directory-title">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Approved identities</p>
        <h2 id="communication-directory-title" className="text-h4">VERITAS directory</h2>
        <p className="text-sm text-neutral-700 mt-1">
          Only active, manager-approved users appear here.
        </p>
      </div>
      <span className="tag tag-neutral">
        {callsEnabled ? 'Internal calls active' : 'Internal calls held'}
      </span>
    </div>

    {!simple && <div className="border border-neutral-300 bg-neutral-100 p-3 mt-4 text-sm">
      <p><strong>Internal calling:</strong> {data.internalSecurity}</p>
      <p className="text-neutral-700 mt-1">
        Presence does not block a direct call. VERITAS attempts each enrolled device for the selected identity.
      </p>
      <p className="text-neutral-700 mt-1">
        Secure messaging storage is prepared, but messaging stays off until its protocol implementation is reviewed and enabled.
      </p>
    </div>}

    {activeCall ? <p className="border border-neutral-300 bg-neutral-100 p-3 mt-4 text-sm">
      The employee directory is hidden during an active call to keep names off the call screen.
    </p> : <>
      <label className="field mt-4" htmlFor="communication-directory-search">
        <span>Find a person or VERITAS ID</span>
      </label>
      <input id="communication-directory-search" className="input" type="search"
        value={query} onChange={(event) => setQuery(event.target.value)}
        placeholder="Search approved users" />
      {entries.length === 0 ? <p className="text-sm text-neutral-700 mt-4">
        No approved users match this search.
      </p> : <ul className="grid gap-2 mt-4">
        {entries.map((entry) => <li key={entry.userId}
          className="border border-neutral-300 bg-bg p-3 flex flex-wrap items-center gap-3">
          <UserGroupIcon className="h-6 w-6 shrink-0 text-accent-700" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="block font-heading font-semibold truncate">{entry.displayName}</span>
            <span className="block text-xs text-neutral-700">
              VERITAS identity · {PRESENCE_LABEL[entry.presence]}
            </span>
            {!simple && <span className="block text-[11px] font-mono text-neutral-600 break-all mt-1">
              {entry.veritasId}
            </span>}
          </span>
          <button type="button" className="btn btn-primary shrink-0"
            disabled={busy || !callsEnabled || !entry.callable || !peerSupported
              || !['offline', 'failed', 'ready'].includes(phoneState)}
            title={!callsEnabled ? 'Internal calling is temporarily unavailable'
              : !peerSupported ? 'Open the enrolled VERITAS mobile app to place a protected peer call'
                : entry.callable ? `Call ${entry.veritasId}` : 'No enrolled recipient device is available'}
            onClick={() => onCall(entry)}>
            <PhoneIcon className="h-5 w-5" aria-hidden /> Call
          </button>
        </li>)}
      </ul>}
    </>}
  </section>;
}

type NativeContactSelection =
  | { kind: 'workforce'; entry: WorkforceDirectoryEntry }
  | { kind: 'saved'; item: SecureContactItem };

function NativeContactDirectory({ data, phoneState, peerSupported, busy, onCall, onCallContact }: {
  data: CallCenterData['communications'];
  phoneState: PhoneState;
  peerSupported: boolean;
  busy: boolean;
  onCall: (entry: WorkforceDirectoryEntry) => void;
  onCallContact: (card: SecureContactCard) => void;
}) {
  const [view, setView] = useState<'list' | 'new' | 'requests' | 'detail' | 'share'>('list');
  const [query, setQuery] = useState('');
  const [connectCode, setConnectCode] = useState('');
  const [shareQuery, setShareQuery] = useState('');
  const [selected, setSelected] = useState<NativeContactSelection | null>(null);
  const [shareCard, setShareCard] = useState<SecureContactCard | null>(null);
  const [secureContacts, setSecureContacts] = useState<SecureContactItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState({
    displayName: '', phone: '', email: '', organization: '', jobTitle: '',
    veritasId: '',
  });
  const activeCall = ['calling', 'connected', 'held', 'ringing'].includes(phoneState);
  const callsEnabled = data.internalCalling === 'enabled';
  const callableState = ['offline', 'failed', 'ready'].includes(phoneState);

  const refreshSecureContacts = useCallback(async () => {
    if (!isNativeMobile()) return;
    setLoading(true); setMessage(null);
    try {
      const channel = await import('../mobile/secureChannel.js');
      setSecureContacts(await channel.loadMobileContacts());
    } catch (cause) {
      setMessage(contactDirectoryMessage(cause));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void refreshSecureContacts(); }, [refreshSecureContacts]);

  const pending = secureContacts.filter((item) => item.state === 'pending');
  const saved = secureContacts.filter((item) => item.state === 'accepted');
  const normalized = query.normalize('NFKC').trim().toLocaleLowerCase();
  const listItems = [
    ...data.directory.map((entry) => ({
      key: `workforce:${entry.userId}`,
      name: entry.displayName,
      secondary: `VERITAS contact · ${PRESENCE_LABEL[entry.presence]}`,
      search: `${entry.displayName} ${entry.veritasId}`.toLocaleLowerCase(),
      selection: { kind: 'workforce', entry } as NativeContactSelection,
    })),
    ...saved.map((item) => ({
      key: `saved:${item.deliveryId}`,
      name: item.card.displayName,
      secondary: item.card.organization || item.card.phone || item.card.email || 'Saved contact',
      search: [item.card.displayName, item.card.phone, item.card.email, item.card.organization,
        item.card.jobTitle, item.card.veritasId].filter(Boolean).join(' ').toLocaleLowerCase(),
      selection: { kind: 'saved', item } as NativeContactSelection,
    })),
  ].filter((item) => !normalized || item.search.includes(normalized))
    .sort((left, right) => left.name.localeCompare(right.name));
  const groups = listItems.reduce<Array<{ letter: string; entries: typeof listItems }>>((all, entry) => {
    const letter = /^[A-Z]$/u.test(entry.name.trim().charAt(0).toLocaleUpperCase())
      ? entry.name.trim().charAt(0).toLocaleUpperCase() : '#';
    const current = all.at(-1);
    if (current?.letter === letter) current.entries.push(entry);
    else all.push({ letter, entries: [entry] });
    return all;
  }, []);
  const exactCode = connectCode.normalize('NFKC').trim().toLocaleUpperCase();
  const connection = data.directory.find((entry) =>
    entry.veritasId.toLocaleUpperCase() === exactCode) ?? null;

  const openSelection = (value: NativeContactSelection) => {
    setSelected(value); setMessage(null); setView('detail');
  };
  const goBack = () => {
    setMessage(null); setShareCard(null); setShareQuery('');
    setView(selected && view === 'share' ? 'detail' : 'list');
  };

  if (view === 'new') return <section aria-labelledby="new-contact-title">
    <ContactBackButton onClick={goBack} label="Contacts" />
    <p className="mt-5 text-h6 uppercase tracking-[.08em] text-accent-700">Private contact</p>
    <h2 id="new-contact-title" className="text-h3 mt-1">New contact</h2>
    <p className="mt-2 text-sm text-neutral-700">
      Details are encrypted on this phone before VERITAS stores the contact envelope.
    </p>
    <form className="mt-5 space-y-4" onSubmit={(event) => {
      event.preventDefault();
      setLoading(true); setMessage(null);
      void import('../mobile/secureChannel.js').then((channel) => channel.saveMobileContact(form)).then(() => {
        setForm({ displayName: '', phone: '', email: '', organization: '', jobTitle: '', veritasId: '' });
        return refreshSecureContacts();
      }).then(() => setView('list')).catch((cause) => setMessage(contactDirectoryMessage(cause)))
        .finally(() => setLoading(false));
    }}>
      <label className="field" htmlFor="contact-name"><span>Name</span>
        <input id="contact-name" className="input" autoComplete="name" required maxLength={100}
          value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} />
      </label>
      <label className="field" htmlFor="contact-phone"><span>US phone number</span>
        <input id="contact-phone" className="input" inputMode="tel" autoComplete="tel-national"
          placeholder="10 digits" maxLength={18} value={form.phone}
          onChange={(event) => setForm({ ...form, phone: event.target.value })} />
      </label>
      <label className="field" htmlFor="contact-email"><span>Email, optional</span>
        <input id="contact-email" className="input" type="email" autoComplete="email" maxLength={254}
          value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="field" htmlFor="contact-organization"><span>Organization</span>
          <input id="contact-organization" className="input" autoComplete="organization" maxLength={100}
            value={form.organization} onChange={(event) => setForm({ ...form, organization: event.target.value })} />
        </label>
        <label className="field" htmlFor="contact-title"><span>Title</span>
          <input id="contact-title" className="input" autoComplete="organization-title" maxLength={100}
            value={form.jobTitle} onChange={(event) => setForm({ ...form, jobTitle: event.target.value })} />
        </label>
      </div>
      <details className="border-y border-neutral-300 py-3">
        <summary className="font-semibold cursor-pointer">VERITAS ID, optional</summary>
        <div className="mt-3 space-y-3">
          <label className="field" htmlFor="contact-veritas-id"><span>VERITAS ID</span>
            <input id="contact-veritas-id" className="input font-mono" autoCapitalize="characters"
              value={form.veritasId} onChange={(event) => setForm({ ...form, veritasId: event.target.value })} />
          </label>
        </div>
      </details>
      {message && <p className="field-error" role="alert">{message}</p>}
      <button type="submit" className="btn btn-primary w-full" disabled={loading || !form.displayName.trim()
        || (!form.phone.trim() && !form.veritasId.trim())}>
        <UserPlusIcon className="h-5 w-5" aria-hidden /> {loading ? 'Saving…' : 'Save contact'}
      </button>
    </form>
  </section>;

  if (view === 'requests') return <section aria-labelledby="contact-requests-title">
    <ContactBackButton onClick={goBack} label="Contacts" />
    <h2 id="contact-requests-title" className="text-h3 mt-5">Received contacts</h2>
    <p className="mt-1 text-sm text-neutral-700">Nothing is added until you approve it.</p>
    {message && <p className="field-error mt-3" role="alert">{message}</p>}
    {loading ? <p className="py-8 text-center">Opening protected contacts…</p>
      : pending.length === 0 ? <p className="py-8 text-center text-neutral-700">No contact requests.</p>
        : <ul className="mt-4 divide-y divide-neutral-300 border-y border-neutral-300">
          {pending.map((item) => <li key={item.deliveryId} className="py-4">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent-100
                font-semibold text-accent-800" aria-hidden>{contactInitials(item.card.displayName)}</span>
              <span className="min-w-0 flex-1">
                <strong className="block truncate">{item.card.displayName}</strong>
                <span className="block text-sm text-neutral-700">Shared by {item.sharedBy}</span>
                <span className="block text-sm text-neutral-700">{item.card.phone || item.card.organization || item.card.email}</span>
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button type="button" className="btn btn-secondary" disabled={loading}
                onClick={() => {
                  setLoading(true); setMessage(null);
                  void import('../mobile/secureChannel.js')
                    .then((channel) => channel.decideMobileContact(item.deliveryId, 'decline'))
                    .then(refreshSecureContacts)
                    .catch((cause) => setMessage(contactDirectoryMessage(cause))).finally(() => setLoading(false));
                }}>Decline</button>
              <button type="button" className="btn btn-primary" disabled={loading}
                onClick={() => {
                  setLoading(true); setMessage(null);
                  void import('../mobile/secureChannel.js')
                    .then((channel) => channel.decideMobileContact(item.deliveryId, 'accept'))
                    .then(refreshSecureContacts)
                    .catch((cause) => setMessage(contactDirectoryMessage(cause))).finally(() => setLoading(false));
                }}>Add to contacts</button>
            </div>
          </li>)}
        </ul>}
  </section>;

  if (view === 'share' && shareCard) {
    const recipientQuery = shareQuery.normalize('NFKC').trim().toLocaleLowerCase();
    const recipients = data.directory.filter((entry) => !recipientQuery
      || `${entry.displayName} ${entry.veritasId}`.toLocaleLowerCase().includes(recipientQuery));
    return <section aria-labelledby="share-contact-title">
      <ContactBackButton onClick={goBack} label={shareCard.displayName} />
      <h2 id="share-contact-title" className="text-h3 mt-5">Share contact</h2>
      <p className="mt-1 text-sm text-neutral-700">
        Choose a VERITAS recipient. They must approve the protected card.
      </p>
      <label className="field mt-4" htmlFor="contact-recipient-search"><span className="sr-only">Find recipient</span>
        <input id="contact-recipient-search" className="input" type="search" value={shareQuery}
          placeholder="Find recipient" onChange={(event) => setShareQuery(event.target.value)} />
      </label>
      {message && <p className="field-error mt-3" role="alert">{message}</p>}
      <ul className="mt-3 divide-y divide-neutral-300 border-y border-neutral-300">
        {recipients.map((entry) => <li key={entry.userId}>
          <button type="button" className="flex min-h-[64px] w-full items-center gap-3 px-2 py-2 text-left"
            disabled={loading} onClick={() => {
              setLoading(true); setMessage(null);
              void import('../mobile/secureChannel.js')
                .then((channel) => channel.shareMobileContact(shareCard, entry.userId))
                .then(({ recipientDisplayName }) => {
                setMessage(`Protected contact sent to ${recipientDisplayName}.`);
              }).catch((cause) => setMessage(contactDirectoryMessage(cause))).finally(() => setLoading(false));
            }}>
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent-100
              font-semibold text-accent-800" aria-hidden>{contactInitials(entry.displayName)}</span>
            <span className="min-w-0 flex-1"><strong className="block truncate">{entry.displayName}</strong>
              <span className="block text-sm text-neutral-700">VERITAS identity</span>
            </span>
            <PaperAirplaneIcon className="h-5 w-5 text-accent-700" aria-hidden />
          </button>
        </li>)}
      </ul>
    </section>;
  }

  if (view === 'detail' && selected) {
    const workforce = selected.kind === 'workforce' ? selected.entry : null;
    const savedContact = selected.kind === 'saved' ? selected.item.card : null;
    const name = workforce?.displayName ?? savedContact!.displayName;
    const target = workforce?.veritasId ?? savedContact?.veritasId ?? savedContact?.phone ?? null;
    const internal = Boolean(workforce || savedContact?.veritasId);
    const recipientReady = workforce ? workforce.callable : true;
    const callAllowed = Boolean(target && callableState && recipientReady
      && (!internal || (callsEnabled && peerSupported)));
    return <section aria-labelledby="native-contact-name">
      <ContactBackButton onClick={goBack} label="Contacts" />
      <div className="py-7 text-center">
        <span className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-accent-100
          font-heading text-h2 font-semibold text-accent-800" aria-hidden>{contactInitials(name)}</span>
        <h2 id="native-contact-name" className="mt-3 text-h2">{name}</h2>
        {savedContact?.jobTitle && <p className="mt-1 text-neutral-700">{savedContact.jobTitle}</p>}
        {savedContact?.organization && <p className="text-sm text-neutral-700">{savedContact.organization}</p>}
        {workforce && <p className="mt-1 text-sm text-neutral-700">{PRESENCE_LABEL[workforce.presence]}</p>}
      </div>
      <button type="button" className="btn btn-primary w-full" disabled={busy || !callAllowed}
        onClick={() => workforce ? onCall(workforce) : onCallContact(savedContact!)}>
        <PhoneIcon className="h-5 w-5" aria-hidden /> Call {target ? formatContactTarget(target) : ''}
      </button>
      {savedContact && <dl className="mt-5 divide-y divide-neutral-300 border-y border-neutral-300 text-sm">
        {savedContact.phone && <ContactDetail label="Phone" value={formatContactTarget(savedContact.phone)} />}
        {savedContact.email && <ContactDetail label="Email" value={savedContact.email} />}
        {savedContact.veritasId && <ContactDetail label="VERITAS ID" value={savedContact.veritasId} mono />}
      </dl>}
      {workforce && <dl className="mt-5 divide-y divide-neutral-300 border-y border-neutral-300 text-sm">
        <ContactDetail label="VERITAS ID" value={workforce.veritasId} mono />
      </dl>}
      {savedContact && <button type="button" className="btn btn-secondary mt-4 w-full" onClick={() => {
        setShareCard(savedContact); setMessage(null); setView('share');
      }}><PaperAirplaneIcon className="h-5 w-5" aria-hidden /> Share securely</button>}
    </section>;
  }

  return <section aria-labelledby="communication-directory-title">
    <div className="flex items-center justify-between gap-2">
      <h2 id="communication-directory-title" className="text-h3">Contacts</h2>
      <div className="flex gap-2">
        <button type="button" className="btn btn-secondary px-3 relative" aria-label="Received contacts"
          onClick={() => { setMessage(null); setView('requests'); }}>
          <InboxIcon className="h-5 w-5" aria-hidden />
          {pending.length > 0 && <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-alert-600 px-1
            text-center text-[11px] leading-5 text-white">{pending.length}</span>}
        </button>
        <button type="button" className="btn btn-primary px-3" aria-label="Add contact"
          onClick={() => { setMessage(null); setView('new'); }}>
          <UserPlusIcon className="h-5 w-5" aria-hidden /> Add
        </button>
      </div>
    </div>
    <label className="field mt-4" htmlFor="native-connect-code"><span>Connect with VERITAS ID</span>
      <div className="flex gap-2">
        <input id="native-connect-code" className="input min-w-0 font-mono" value={connectCode}
          autoCapitalize="characters" placeholder="VER-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
          onChange={(event) => setConnectCode(event.target.value)} />
        <button type="button" className="btn btn-secondary shrink-0" disabled={!connection}
          onClick={() => connection && openSelection({ kind: 'workforce', entry: connection })}>Connect</button>
      </div>
    </label>
    {connectCode.trim() && !connection && <p className="mt-1 text-xs text-neutral-700">
      Enter an exact approved VERITAS ID.
    </p>}
    <div className="sticky top-[64px] z-10 mt-4 bg-bg pb-3">
      <label className="field" htmlFor="native-contact-search"><span className="sr-only">Search contacts</span>
        <input id="native-contact-search" className="input" type="search" value={query}
          onChange={(event) => setQuery(event.target.value)} placeholder="Search contacts"
          autoComplete="off" />
      </label>
    </div>
    {message && <p className="field-error mb-3" role="alert">{message}</p>}
    {activeCall ? <p className="border border-neutral-300 bg-neutral-100 p-3 text-sm">Contacts are hidden during a call.</p>
      : loading && listItems.length === 0 ? <p className="py-8 text-center">Opening protected contacts…</p>
        : groups.length === 0 ? <p className="py-8 text-center text-neutral-700">No matching contacts.</p>
          : <div className="divide-y divide-neutral-300 border-y border-neutral-300">
            {groups.map((group) => <section key={group.letter} aria-label={`${group.letter} contacts`}>
              <h3 className="sticky top-[124px] bg-neutral-100 px-2 py-1 text-xs font-semibold text-accent-800">
                {group.letter}
              </h3>
              {group.entries.map((entry) => <button key={entry.key} type="button"
                className="flex min-h-[64px] w-full items-center gap-3 px-2 py-2 text-left
                  hover:bg-accent-100 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-600"
                onClick={() => openSelection(entry.selection)}>
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent-100
                  font-semibold text-accent-800" aria-hidden>{contactInitials(entry.name)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">{entry.name}</span>
                  <span className="block truncate text-sm text-neutral-700">{entry.secondary}</span>
                </span>
                <ChevronDownIcon className="h-5 w-5 -rotate-90 text-neutral-600" aria-hidden />
              </button>)}
            </section>)}
          </div>}
  </section>;
}

function ContactBackButton({ onClick, label }: { onClick: () => void; label: string }) {
  return <button type="button" className="btn btn-ghost px-2" onClick={onClick}>
    <ChevronDownIcon className="h-5 w-5 rotate-90" aria-hidden /> {label}
  </button>;
}

function ContactDetail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return <div className="py-3"><dt className="text-xs text-neutral-700">{label}</dt>
    <dd className={`mt-1 break-all ${mono ? 'font-mono text-xs' : 'font-semibold'}`}>{value}</dd>
  </div>;
}

function MyDayPanel({ data }: { data: CallCenterData }) {
  const definitions = new Map(data.metricDefinitions.map((definition) => [definition.key, definition]));
  const metrics = [
    ['calls_offered', data.dailyProgress.callsOffered],
    ['calls_answered', data.dailyProgress.callsAnswered],
    ['calls_handled', data.dailyProgress.callsHandled],
    ['appointments_created', data.dailyProgress.appointmentsCreated],
  ] as const;
  return <div className="space-y-5">
    <section aria-labelledby="my-day-schedule">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 id="my-day-schedule" className="text-h4">My schedule</h2>
          <p className="text-sm text-neutral-700">Displayed in {data.schedule.timezone.replace('_', ' ')}</p>
        </div>
      </div>
      {data.schedule.items.length === 0
        ? <p className="border border-neutral-300 bg-neutral-100 p-3 mt-2 text-sm text-neutral-700">
            No shift, break, meeting, training, leave, or appointment is assigned yet.
          </p>
        : <ol className="border-y border-neutral-300 divide-y divide-neutral-300 mt-2">
            {data.schedule.items.map((item) => <li key={item.id}
              className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 py-3">
              <span className="min-w-0">
                <span className="block font-semibold capitalize">{item.type.replace('_', ' ')}</span>
                <span className="block text-sm text-neutral-700">{formatScheduleRange(item, data.schedule.timezone)}</span>
              </span>
              <span className="tag tag-neutral self-start capitalize">{item.status}</span>
            </li>)}
          </ol>}
    </section>

    <section aria-labelledby="my-day-metrics">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="my-day-metrics" className="text-h4">Daily metrics</h2>
        <span className="tag tag-neutral">{data.dailyProgress.status}</span>
      </div>
      <div className="grid grid-cols-1 min-[720px]:grid-cols-2 gap-2 mt-2">
        {metrics.map(([key, value]) => {
          const definition = definitions.get(key);
          return <article key={key} className="border border-neutral-300 bg-neutral-100 p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold">{definition?.label ?? key.replaceAll('_', ' ')}</h3>
                <p className="font-heading text-h3 tabular-nums">{value}</p>
              </div>
              {definition && <span className="text-xs text-neutral-700">v{definition.version}</span>}
            </div>
            {definition && <details className="mt-2 text-sm">
              <summary className="cursor-pointer font-semibold text-accent-700">How this is calculated</summary>
              <dl className="grid gap-2 mt-2">
                <div><dt className="text-neutral-700">Calculation</dt><dd>{definition.formula}</dd></div>
                <div><dt className="text-neutral-700">Denominator</dt><dd>{definition.denominator ?? 'Not applicable'}</dd></div>
                <div><dt className="text-neutral-700">Exclusions</dt><dd>{definition.exclusions}</dd></div>
                <div><dt className="text-neutral-700">Refresh</dt><dd>Every {definition.refreshSeconds} seconds</dd></div>
              </dl>
            </details>}
          </article>;
        })}
      </div>
    </section>
  </div>;
}

function ConversationCoach({ segments, active, playbook, onEdit }: {
  segments: TranscriptSegment[]; active: boolean; playbook: PlaybookEntry[];
  onEdit?: () => void;
}) {
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const callerWords = segments.filter((segment) => segment.speaker === 'caller')
    .slice(-3).map((segment) => segment.text).join(' ').toLowerCase();
  const suggestions = useMemo(() => rankPlaybook(callerWords, playbook).slice(0, 3), [callerWords, playbook]);
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return playbook;
    return playbook.filter((entry) => [entry.category, entry.title, entry.response,
      entry.followUp, ...entry.triggers].join(' ').toLowerCase().includes(normalized));
  }, [query, playbook]);

  async function copy(entry: PlaybookEntry) {
    await navigator.clipboard.writeText(`${entry.response} ${entry.followUp}`);
    setCopied(entry.id);
    window.setTimeout(() => setCopied((current) => current === entry.id ? null : current), 1800);
  }

  return <section className="border border-accent-300 bg-gradient-to-br from-accent-100 to-neutral-100 p-4"
    aria-labelledby="coach-title">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-h6 uppercase tracking-[.08em] text-accent-700">Operator only</p>
        <h2 id="coach-title" className="text-h3">Conversation coach</h2>
        <p className="text-sm text-neutral-700 mt-1">Approved language matched to the caller's latest words.</p>
      </div>
      <div className="flex items-center gap-2">
        <span className="tag tag-neutral">{active ? 'Live guidance' : 'Ready'}</span>
        {onEdit && <button type="button" className="btn btn-secondary" onClick={onEdit}>
          Edit playbook
        </button>}
      </div>
    </div>

    <div className="mt-4">
      <h3 className="text-h4">Suggested next</h3>
      <div className="grid grid-cols-1 min-[720px]:grid-cols-2 min-[1440px]:grid-cols-3 gap-2 mt-2"
        data-testid="coach-suggestions">
        {suggestions.map((entry) => <article key={entry.id}
          className="min-w-0 border border-neutral-300 bg-bg p-3 flex flex-col"
          data-testid="coach-suggestion">
          <div className="min-w-0 flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
            <span className="min-w-0 break-words text-xs font-semibold uppercase tracking-[.08em] text-accent-700"
              data-testid="coach-suggestion-category">{entry.category}</span>
            <button type="button" className="shrink-0 whitespace-nowrap text-xs underline"
              data-testid="coach-suggestion-copy" onClick={() => void copy(entry)}>
              {copied === entry.id ? 'Copied' : 'Copy'}
            </button>
          </div>
          <h4 className="break-words font-heading font-semibold mt-1">{entry.title}</h4>
          <p className="break-words text-sm mt-2">“{entry.response}”</p>
          <p className="break-words text-sm text-neutral-700 mt-2"><strong>Ask:</strong> {entry.followUp}</p>
        </article>)}
      </div>
    </div>

    <details className="mt-4 border-t border-neutral-300 pt-3">
      <summary className="font-semibold cursor-pointer">Browse the full call guide</summary>
      <label className="field mt-3" htmlFor="playbook-search"><span>Search scripts and questions</span></label>
      <input id="playbook-search" className="input" type="search" value={query}
        placeholder="Try sponsorship, appointment, pricing, or complaint"
        onChange={(event) => setQuery(event.target.value)} />
      <div className="grid desktop:grid-cols-2 gap-2 mt-3 max-h-96 overflow-y-auto">
        {filtered.map((entry) => <article key={entry.id} className="border border-neutral-300 bg-bg p-3">
          <p className="text-xs font-semibold uppercase tracking-[.08em] text-accent-700">{entry.category}</p>
          <h4 className="font-heading font-semibold">{entry.title}</h4>
          <p className="text-sm mt-1">{entry.response}</p>
          <p className="text-sm text-neutral-700 mt-1"><strong>Next question:</strong> {entry.followUp}</p>
        </article>)}
      </div>
    </details>
    <p className="text-xs text-neutral-700 mt-3">
      Guidance is private to the operator. Nothing is spoken or sent automatically.
    </p>
  </section>;
}

function rankPlaybook(callerWords: string, playbook: PlaybookEntry[]): PlaybookEntry[] {
  if (!callerWords) return playbook.slice(0, 3);
  const words = new Set(callerWords.replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean));
  return playbook.map((entry, position) => ({
    entry,
    position,
    score: entry.triggers.reduce((total, trigger) => total +
      (trigger.includes(' ') ? (callerWords.includes(trigger) ? 4 : 0) : (words.has(trigger) ? 2 : 0)), 0),
  })).sort((left, right) => right.score - left.score || left.position - right.position)
    .map((ranked) => ranked.entry);
}

function CaptionPanel({ segments, status, error, retentionDays, active, paused, onTogglePause }: {
  segments: TranscriptSegment[];
  status: 'idle' | 'starting' | 'listening' | 'delayed';
  error: string | null;
  retentionDays: number;
  active: boolean;
  paused: boolean;
  onTogglePause: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const logRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [segments]);
  const label = paused ? 'Paused' : status === 'listening' ? 'Listening'
    : status === 'starting' ? 'Starting'
      : status === 'delayed' ? 'Delayed' : active ? 'Waiting' : 'Ready';
  const latest = segments.at(-1);
  const currentCaption = paused
    ? 'Translation paused'
    : latest?.text
      ?? (active ? 'Listening for the next speaker…' : 'Translations appear here during a call.');
  return <section className="border border-accent-400 bg-bg shadow-sm"
    aria-labelledby="captions-title">
    <div className="p-3">
      <div className="flex items-center gap-2 min-w-0">
        <ChatBubbleBottomCenterTextIcon className="h-5 w-5 shrink-0 text-accent-700" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <h2 id="captions-title" className="text-base font-heading font-semibold">Live translation</h2>
            <span className="text-xs font-semibold text-accent-700">Spanish → English</span>
            <span className="text-[10px] font-bold uppercase tracking-[.12em] text-neutral-700">Operator only</span>
          </div>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-semibold ${
          status === 'delayed' ? 'border-alert-400 bg-alert-100 text-alert-800'
            : 'border-neutral-300 bg-neutral-100 text-neutral-800'
        }`}>{label}</span>
      </div>

      <div className="mt-2 flex items-start gap-2 border-l-2 border-accent-500 bg-accent-100 px-3 py-2 min-h-touch">
        {latest && <span className="shrink-0 text-xs font-bold uppercase tracking-[.06em] text-accent-800 pt-0.5">
          {latest.speaker === 'agent' ? 'You' : 'Caller'}
        </span>}
        <p className="min-w-0 flex-1 text-sm leading-5 line-clamp-2" aria-live={expanded ? 'off' : 'polite'}>
          {currentCaption}
          {latest?.reviewRequired && <span className="inline-flex items-center gap-1 ml-2 text-xs text-alert-700">
            <ExclamationTriangleIcon className="h-4 w-4" aria-hidden /> Verify wording
          </span>}
        </p>
      </div>

      <div className="mt-2 flex items-center justify-end gap-2">
        {active && <button type="button" className="btn btn-secondary px-3" onClick={onTogglePause}>
          {paused ? 'Resume' : 'Pause'}
        </button>}
        <button type="button" className="btn btn-ghost px-3" aria-expanded={expanded}
          aria-controls="live-transcript-details" onClick={() => setExpanded((current) => !current)}>
          {expanded ? 'Hide transcript' : 'Show transcript'}
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden />
        </button>
      </div>
    </div>

    {expanded && <div id="live-transcript-details" className="border-t border-neutral-300 p-3">
      <div ref={logRef} className="max-h-[40vh] overflow-y-auto border border-neutral-300 bg-neutral-100" role="log"
        aria-live="polite" aria-relevant="additions text" aria-label="Live call transcript">
        {segments.length === 0 ? <p className="p-3 text-sm text-neutral-700">
          {paused ? 'Translation is paused. Select Resume when the caller is ready.'
            : active ? 'Listening. Each speaker will appear here.' : 'Start or answer a call to begin.'}
        </p> : <ol className="divide-y divide-neutral-300">
          {segments.map((segment) => <li key={segment.id} className="p-3 grid grid-cols-[64px_1fr] gap-2">
            <span className="text-xs font-bold uppercase tracking-[.06em] text-accent-700">
              {segment.speaker === 'agent' ? 'You' : 'Caller'}
            </span>
            <span className="text-sm">
              {segment.text}
              {segment.reviewRequired && <span className="inline-flex items-center gap-1 ml-2 text-xs text-alert-700">
                <ExclamationTriangleIcon className="h-4 w-4" aria-hidden /> Verify wording
              </span>}
            </span>
          </li>)}
        </ol>}
      </div>
      <p className="text-xs text-neutral-700 mt-2">
        No automated announcement is played. Follow the approved disclosure policy. Protected text is kept for {retentionDays} days. Raw audio is not stored.
        Confirm names, addresses, and numbers aloud.
      </p>
    </div>}
    {error && <p className="field-error mx-3 mb-3" role="alert">{error}</p>}
  </section>;
}

function SecurityPanel({ data }: { data: CallCenterData }) {
  return <section className="border-b border-neutral-300 pb-4" aria-labelledby="call-security-title">
    <h2 id="call-security-title" className="text-h4 mb-2">Call security</h2>
    <dl className="grid grid-cols-1 desktop:grid-cols-3 gap-2 text-sm">
      <div><dt className="text-neutral-700">Signaling</dt><dd>{data.security.signaling}</dd></div>
      <div><dt className="text-neutral-700">Live media</dt><dd>{data.security.media}</dd></div>
      <div><dt className="text-neutral-700">Stored call data</dt><dd>{data.security.protectedData}</dd></div>
    </dl>
    {!data.security.mediaE2ee && <p className="text-sm text-neutral-700 mt-2">
      Queue calls are encrypted on each network leg. FreeSWITCH must process audio for IVR, transfer, supervision, and recording, so these calls are not endpoint-to-endpoint encrypted.
    </p>}
  </section>;
}

function QueuePanel({ queues }: { queues: CallCenterData['queues'] }) {
  return <section aria-labelledby="queue-title">
    <h2 id="queue-title" className="text-h4 mb-2">Queues</h2>
    {queues.length === 0 ? <p className="text-sm text-neutral-700">No active queues.</p> : <ul className="divide-y divide-neutral-300 border-y border-neutral-300">
      {queues.map((queue) => <li key={queue.id} className="py-3 flex items-center gap-3">
        <SpeakerWaveIcon className="h-5 w-5 text-accent-700 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1"><span className="block font-semibold truncate">{queue.name}</span>
          <span className="block text-sm text-neutral-700">Extension {queue.extension}</span></span>
        <span className="text-sm text-right"><strong className="block tabular-nums">{queue.waiting}</strong><span className="text-neutral-700">waiting</span></span>
        <span className="text-sm text-right"><strong className="block tabular-nums">{queue.active}</strong><span className="text-neutral-700">active</span></span>
      </li>)}
    </ul>}
  </section>;
}

function HistoryPanel({ calls, onRefresh, allowTranscripts = true }: {
  calls: CallCenterCall[]; onRefresh: () => Promise<void>; allowTranscripts?: boolean;
}) {
  const [openCallId, setOpenCallId] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptSegment[]>([]);
  const [loading, setLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);

  async function toggleTranscript(call: CallCenterCall) {
    if (openCallId === call.id) {
      setOpenCallId(null);
      setTranscript([]);
      return;
    }
    setOpenCallId(call.id);
    setTranscript([]);
    setTranscriptError(null);
    setLoading(true);
    try {
      const response = await api.callCenterTranscript(call.id);
      setTranscript(response.segments);
    } catch (cause) {
      setTranscriptError(apiMessage(cause));
    } finally { setLoading(false); }
  }

  return <section aria-labelledby="history-title">
    <div className="flex items-center justify-between gap-2 mb-2"><h2 id="history-title" className="text-h4">Recent calls</h2>
      <button type="button" className="btn btn-ghost px-2" onClick={() => void onRefresh()}>
        <ArrowPathIcon className="h-5 w-5" aria-hidden /> Refresh</button></div>
    {calls.length === 0 ? <p className="text-sm text-neutral-700">No calls have been logged.</p> : <ul className="divide-y divide-neutral-300 border-y border-neutral-300">
      {calls.map((call) => <li key={call.id} className="py-3">
        <div className="flex gap-3 items-start">
          {call.direction === 'inbound' ? <PhoneArrowDownLeftIcon className="h-5 w-5 text-accent-700 shrink-0" /> : <PhoneArrowUpRightIcon className="h-5 w-5 text-accent-700 shrink-0" />}
          <span className="min-w-0 flex-1"><span className="block font-semibold truncate">{operatorNumber(call.remoteIdentity) || 'Private number'}</span>
            <span className="block text-sm text-neutral-700">{formatCallTime(call.startedAt)} · {call.state.replace('_', ' ')}</span></span>
          {call.disposition && <span className="text-sm text-neutral-700">{call.disposition}</span>}
          {allowTranscripts && call.transcriptCount > 0 && <button type="button" className="btn btn-ghost px-2"
            aria-expanded={openCallId === call.id} onClick={() => void toggleTranscript(call)}>
            {openCallId === call.id ? 'Hide' : `Transcript (${call.transcriptCount})`}
          </button>}
        </div>
        {openCallId === call.id && <div className="mt-3 ml-8 border-l-2 border-accent-300 pl-3">
          {loading ? <p className="text-sm text-neutral-700">Loading protected transcript…</p>
            : transcriptError ? <p className="field-error" role="alert">{transcriptError}</p>
              : transcript.length === 0 ? <p className="text-sm text-neutral-700">No spoken words were captured.</p>
                : <ol className="space-y-2 max-h-80 overflow-y-auto">
                  {transcript.map((segment) => <li key={segment.id} className="text-sm">
                    <span className="font-semibold">{segment.speaker === 'agent' ? 'Operator' : 'Caller'}:</span>{' '}
                    {segment.text}
                    {segment.reviewRequired && <span className="text-alert-700"> [verify]</span>}
                  </li>)}
                </ol>}
        </div>}
      </li>)}
    </ul>}
  </section>;
}

function CallCenterSkeleton() {
  return <div className="pt-6" role="status" aria-label="Loading call center"><div className="h-8 w-48 bg-neutral-300 animate-pulse" /><div className="mt-5 h-80 max-w-[420px] bg-neutral-200 animate-pulse" /></div>;
}

function formatCallTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date);
}

function formatDuration(totalSeconds: number) {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const remainder = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${remainder}s`;
  return `${remainder}s`;
}

function formatScheduleRange(event: ScheduleEvent, timeZone: string) {
  const startsAt = new Date(event.startsAt);
  const endsAt = new Date(event.endsAt);
  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
    return 'Time unavailable';
  }
  const date = new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone,
  }).format(startsAt);
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric', minute: '2-digit', timeZone,
  });
  return `${date}, ${time.format(startsAt)} to ${time.format(endsAt)}`;
}

function normalizePhone(value: string) {
  const digits = value.replace(/[^0-9]/g, '');
  return (digits.length > 10 && digits.startsWith('1') ? digits.slice(1) : digits).slice(0, 10);
}

function contactInitials(value: string) {
  return value.trim().split(/\s+/u).slice(0, 2).map((part) => part.charAt(0).toLocaleUpperCase()).join('') || '?';
}

function formatContactTarget(value: string) {
  const digits = value.replace(/[^0-9]/gu, '');
  if (digits.length !== 10) return value;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function contactDirectoryMessage(cause: unknown) {
  const reason = cause instanceof Error ? cause.message : '';
  if (reason === 'mobile_activation_required') return 'Unlock the VERITAS phone, then open Contacts again.';
  if (reason === 'contact_recipient_not_found') return 'That VERITAS recipient is not available.';
  if (reason === 'contact_share_limit_reached') return 'The protected sharing limit was reached. Try again tomorrow.';
  if (reason === 'contact_already_delivered') return 'That contact was already sent to this person.';
  if (reason === 'contact_request_not_found') return 'That contact request is no longer available.';
  return reason || 'The protected contact request could not be completed.';
}

function operatorNumber(value: string) {
  const digits = value.replace(/[^0-9]/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return value;
}

function isUsPhoneNumber(value: string) {
  return /^[2-9][0-9]{9}$/u.test(value);
}

function isVeritasId(value: string) {
  return /^VER-(?:[0-9A-F]{4}-){7}[0-9A-F]{4}$/u.test(
    value.normalize('NFKC').trim().toLocaleUpperCase(),
  );
}

function peerCallMessage(cause: unknown) {
  const reason = cause instanceof Error ? cause.message : '';
  if (reason === 'peer_recipient_offline') return 'That VERITAS ID has no enrolled device available.';
  if (reason === 'peer_recipient_not_found') return 'That VERITAS ID is not approved or cannot be found.';
  if (reason === 'peer_recipient_devices_changed') return 'The recipient device list changed. Try the call again.';
  if (reason === 'peer_call_limit_reached') return 'The protected call limit was reached. Try again later.';
  if (reason === 'invalid_veritas_id') return 'Enter a complete VERITAS ID.';
  return reason || 'The protected peer call could not be completed.';
}

function apiMessage(cause: unknown) {
  if (cause instanceof ApiError) {
    if (cause.reason === 'call_center_not_configured') return 'The secure telephony service is not configured.';
    if (cause.reason === 'agent_not_provisioned') return 'Your account does not have a call center extension.';
    if (cause.reason === 'internal_recipient_not_found') return 'That person has no active VERITAS calling extension.';
    if (cause.reason === 'cannot_call_own_extension') return 'Choose another VERITAS contact.';
    if (cause.reason === 'communication_access_not_approved') {
      return 'A manager must approve this VERITAS identity before the phone can connect.';
    }
    if (cause.status === 403) return 'Your account does not have permission to use the call center.';
  }
  return cause instanceof Error ? cause.message : 'The phone request failed.';
}
